import { useEffect, useMemo, useState } from 'react';
import { Header, AdDataDrawer } from '@/components';
import { useAppStore } from '@/stores/appStore';
import { loadCompanies } from '@/services/contacts';
import { API_CONFIG } from '@/services/api';
import { enqueueScrapeJob } from '@/services/scrapeQueue';
import { researchContact } from '@/services/research';
import { addHotListEntityDb } from '@/services/hotLists';
import { getAccountSettings } from '@/services/account';
import { Search, RefreshCw, X, Mail, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

type CompanyRow = {
  companyId: string;
  companyName: string;
  website?: string;
  facebook?: string;
  linkedin?: string;
};

type AdCacheEntry = {
  results: any[];
  updatedAt: string;
};

type SiteCacheEntry = {
  data: unknown[];
  scrapedAt: string;
};

type KeywordRun = {
  key: string;
  term: string;
  source: KeywordSource;
  mode?: ListenerMode;
  status: 'idle' | 'running' | 'succeeded' | 'failed';
  results: Array<Record<string, unknown>>;
  error?: string;
};

type KeywordSource =
  | 'facebook'
  | 'google'
  | 'linkedin_ads'
  | 'instagram_posts'
  | 'instagram_reels'
  | 'linkedin_listeners'
  | 'facebook_listeners'
  | 'tiktok_listeners'
  | 'x_listeners'
  | 'google_trending'
  | 'instagram_trending'
  | 'facebook_trending'
  | 'linkedin_viral'
  | 'tiktok_trending'
  | 'x_trending';
type ListenerMode = 'keyword' | 'hashtag';
type TikTokMode = 'terms' | 'profiles' | 'post_urls';
type ListenerScraper = 'auto' | 'linkedin_url' | 'instagram_direct' | 'instagram_profile' | 'facebook_post_urls' | 'facebook_page_feed';
type ListenerSortField = 'date' | 'author' | 'comments';
type SortDirection = 'asc' | 'desc';

type ListenerComment = {
  id: string;
  postUrl: string;
  platform?: string;
  authorName: string;
  authorUsername?: string;
  authorProfileId?: string;
  authorProfileUrl: string;
  authorProfilePicture?: string;
  authorTitle?: string;
  authorDescription?: string;
  location?: string;
  email?: string;
  text: string;
  postedAt: string;
  likesCount?: number;
  repliesCount?: number;
};

type CommentsModalState = {
  open: boolean;
  postUrl: string;
  postLabel: string;
};

type ListenerCatalogItem = {
  userEmail: string;
  createdAtKey: string;
  createdAt: string;
  catalogId: string;
  itemType: string;
  term?: string;
  source?: string;
  mode?: string;
  runId?: string | null;
  datasetId?: string | null;
  storedDatasetId?: string | null;
  selectedUrls?: string[];
  meta?: Record<string, unknown>;
};

type ListenerScheduleModalState = {
  open: boolean;
  name: string;
  days: string[];
  hour: number;
  autoCommentEnabled: boolean;
  autoCommentText: string;
  autoCommentMaxPosts: number;
  saving: boolean;
  error: string;
};

type ListenerScheduleItem = {
  scheduleId: string;
  name?: string;
  scheduleDays?: string[];
  scheduleHour?: number;
  timezone?: string;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  listenerConfig?: Record<string, unknown>;
  enabled?: boolean;
};

type AdDataTab = 'companies' | 'keywords' | 'listeners' | 'groups';
type GroupPlatform = 'linkedin' | 'facebook' | 'reddit' | 'instagram';
type GroupAction = 'listening' | 'comment_scraping' | 'member_scraping' | 'posting' | 'commenting';

const tabPathMap: Record<AdDataTab, string> = {
  companies: '/ad-data',
  listeners: '/social-listener',
  keywords: '/trending-keyword-research',
  groups: '/group-scraping',
};

const resolveAdDataTab = (pathname: string, search: string): AdDataTab => {
  if (pathname === '/social-listener') return 'listeners';
  if (pathname === '/trending-keyword-research') return 'keywords';
  if (pathname === '/group-scraping') return 'groups';
  const tab = new URLSearchParams(search).get('tab');
  if (tab === 'keywords' || tab === 'listeners' || tab === 'companies' || tab === 'groups') return tab;
  return 'companies';
};

const buildKeywordRunKey = (term: string, source: KeywordSource, mode?: ListenerMode): string =>
  `${term.trim().toLowerCase()}|${source}|${mode || 'none'}`;

const STORAGE_KEY = 'profitibull_ad_data_keywords_v1';
const KEYWORD_HISTORY_VISIBILITY_KEY = 'profitibull_ad_data_keyword_history_visible_v1';
const KEYWORD_RESULTS_PAGE_SIZE = 25;
const PEPAI_CAMPAIGN_STORAGE_KEY = 'profitibull_pepai_campaign_id';

function loadStoredKeywords(userEmail: string): KeywordRun[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${userEmail}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveStoredKeywords(userEmail: string, runs: KeywordRun[]) {
  try {
    localStorage.setItem(`${STORAGE_KEY}:${userEmail}`, JSON.stringify(runs));
  } catch {
    // ignore
  }
}

const normalizeWebsiteUrl = (input: string): string => {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const parseDelimitedList = (value: string): string[] =>
  String(value || '')
    .split(/[\n,]+/)
    .map((v) => v.trim())
    .filter(Boolean);

const extractEmailAddress = (value: unknown): string => {
  const raw = String(value ?? '');
  const match = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match?.[0]?.trim() ?? '';
};

const extractLinkedinOwner = (url: string) => {
  if (!url) return '';
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const parts = u.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('company');
    if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    return parts[parts.length - 1] || '';
  } catch {
    return '';
  }
};

export function AdData() {
  const { userEmail, adminToken, credits } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [perPage, setPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<AdDataTab>(() => resolveAdDataTab(location.pathname, location.search));

  const [scrapeLoading, setScrapeLoading] = useState<Record<string, { website?: boolean; products?: boolean }>>({});
  const [adLoading, setAdLoading] = useState<Record<string, { google?: boolean; facebook?: boolean; linkedin?: boolean }>>({});
  const [adStatus, setAdStatus] = useState<Record<string, string>>({});
  const [adCache, setAdCache] = useState<Record<string, { google?: AdCacheEntry; facebook?: AdCacheEntry; linkedin?: AdCacheEntry }>>({});
  const [adDrawer, setAdDrawer] = useState<{ open: boolean; companyName: string; source: 'google' | 'facebook' | 'linkedin'; data: any }>({
    open: false,
    companyName: '',
    source: 'google',
    data: null,
  });
  const [siteCache, setSiteCache] = useState<Record<string, { website?: SiteCacheEntry; ecommerce?: SiteCacheEntry }>>({});
  const [dataDrawer, setDataDrawer] = useState<{ companyId: string; companyName: string; type: 'website' | 'ecommerce' } | null>(null);
  const [showRawDrawer, setShowRawDrawer] = useState(false);

  const [keywordInput, setKeywordInput] = useState('');
  const [keywordSource, setKeywordSource] = useState<KeywordSource>('facebook');
  const [linkedinViralTargetPosts, setLinkedinViralTargetPosts] = useState(100);
  const [listenerMode, setListenerMode] = useState<ListenerMode>('keyword');
  const [facebookListenerLocation, setFacebookListenerLocation] = useState('');
  const [tiktokMode, setTikTokMode] = useState<TikTokMode>('terms');
  const [tiktokProfilesInput, setTikTokProfilesInput] = useState('');
  const [tiktokPostUrlsInput, setTikTokPostUrlsInput] = useState('');
  const [tiktokSearchQueriesInput, setTikTokSearchQueriesInput] = useState('');
  const [listenerScraper, setListenerScraper] = useState<ListenerScraper>('auto');
  const [listenerSortField, setListenerSortField] = useState<ListenerSortField>('date');
  const [listenerSortDirection, setListenerSortDirection] = useState<SortDirection>('desc');
  const [keywordSortField, setKeywordSortField] = useState<ListenerSortField>('comments');
  const [keywordSortDirection, setKeywordSortDirection] = useState<SortDirection>('desc');
  const [keywordLanguageFilter, setKeywordLanguageFilter] = useState<string>('all');
  const [keywordResultsPage, setKeywordResultsPage] = useState(1);
  const [keywordCompanyId, setKeywordCompanyId] = useState('');
  const [linkedinOwner, setLinkedinOwner] = useState('');
  const [keywordRuns, setKeywordRuns] = useState<KeywordRun[]>([]);
  const [activeKeyword, setActiveKeyword] = useState<string | null>(null);
  const [selectedListenerUrls, setSelectedListenerUrls] = useState<string[]>([]);
  const [listenerDeepResults, setListenerDeepResults] = useState<Record<string, Array<Record<string, unknown>>>>({});
  const [listenerDeepStatus, setListenerDeepStatus] = useState<'idle' | 'running' | 'succeeded' | 'failed'>('idle');
  const [listenerDeepError, setListenerDeepError] = useState<string>('');
  const [listenerAutoCommentEnabled, setListenerAutoCommentEnabled] = useState(false);
  const [listenerAutoCommentText, setListenerAutoCommentText] = useState('');
  const [listenerAutoCommentMaxPosts, setListenerAutoCommentMaxPosts] = useState(5);
  const [listenerAutoCommentStatus, setListenerAutoCommentStatus] = useState('');
  const [commentPage, setCommentPage] = useState(1);
  const [commentActionStatus, setCommentActionStatus] = useState<Record<string, 'idle' | 'running' | 'succeeded' | 'failed'>>({});
  const [postCommentStatus, setPostCommentStatus] = useState<Record<string, 'idle' | 'running' | 'succeeded' | 'failed'>>({});
  const [commentConnectors, setCommentConnectors] = useState<{ linkedin: boolean; instagram: boolean }>({
    linkedin: false,
    instagram: false,
  });
  const [commentsModal, setCommentsModal] = useState<CommentsModalState>({ open: false, postUrl: '', postLabel: '' });
  const [catalogItems, setCatalogItems] = useState<ListenerCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [listenerSchedules, setListenerSchedules] = useState<ListenerScheduleItem[]>([]);
  const [listenerSchedulesLoading, setListenerSchedulesLoading] = useState(false);
  const [listenerSchedulesError, setListenerSchedulesError] = useState('');
  const [showKeywordHistory, setShowKeywordHistory] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(KEYWORD_HISTORY_VISIBILITY_KEY);
      return raw == null ? true : raw === 'true';
    } catch {
      return true;
    }
  });
  const [listenerScheduleModal, setListenerScheduleModal] = useState<ListenerScheduleModalState>({
    open: false,
    name: '',
    days: [],
    hour: 9,
    autoCommentEnabled: false,
    autoCommentText: '',
    autoCommentMaxPosts: 5,
    saving: false,
    error: '',
  });

  const [groupPlatform, setGroupPlatform] = useState<GroupPlatform>('linkedin');
  const [groupAction, setGroupAction] = useState<GroupAction>('listening');
  const [groupQuery, setGroupQuery] = useState('');
  const [groupAgentId, setGroupAgentId] = useState('');
  const [groupPostText, setGroupPostText] = useState('');
  const [groupCommentText, setGroupCommentText] = useState('');
  const [groupRunStatus, setGroupRunStatus] = useState('');

  const refreshCommentGuardrails = async () => {
    if (!userEmail || !adminToken) return;
    try {
      const res = await getAccountSettings({ email: userEmail, token: adminToken });
      if (!res.success || !res.integrations || !res.account) return;
      setCommentConnectors({
        linkedin: Boolean(res.integrations.linkedInCommentAgentId?.valid),
        instagram: Boolean(res.integrations.instagramCommentAgentId?.valid),
      });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const tab = resolveAdDataTab(location.pathname, location.search);
    setActiveTab(tab);
    if (location.pathname === '/ad-data') {
      const qsTab = new URLSearchParams(location.search).get('tab');
      if (qsTab === 'listeners') {
        navigate('/social-listener', { replace: true });
      } else if (qsTab === 'keywords') {
        navigate('/trending-keyword-research', { replace: true });
      } else if (qsTab === 'groups') {
        navigate('/group-scraping', { replace: true });
      }
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const listenerSources: KeywordSource[] = ['instagram_posts', 'instagram_reels', 'linkedin_listeners', 'facebook_listeners', 'tiktok_listeners', 'x_listeners'];
    if (activeTab === 'listeners' && !listenerSources.includes(keywordSource)) {
      setKeywordSource('instagram_posts');
    }
    if (activeTab === 'keywords' && listenerSources.includes(keywordSource)) {
      setKeywordSource('facebook');
    }
  }, [activeTab, keywordSource]);

  useEffect(() => {
    if (activeTab !== 'listeners') return;
    refreshCommentGuardrails();
  }, [activeTab, userEmail, adminToken]);

  const switchTab = (tab: AdDataTab) => {
    setActiveTab(tab);
    navigate(tabPathMap[tab]);
  };

  const runGroupWorkflow = async (action: GroupAction) => {
    if (!userEmail) return;
    const urls = parseDelimitedList(groupQuery).filter((v) => v.startsWith('http'));
    setGroupRunStatus(`Starting ${action.replace('_', ' ')}...`);
    try {
      const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/groups/workflow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          platform: groupPlatform,
          action,
          groupUrl: groupQuery.trim(),
          urls,
          startUrls: urls,
          searchTerms: parseDelimitedList(groupQuery).filter((v) => !v.startsWith('http')),
          agentId: groupAgentId.trim() || undefined,
          postText: groupPostText.trim() || groupQuery.trim(),
          commentText: groupCommentText.trim() || groupQuery.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Group workflow failed');
      setGroupAction(action);
      setGroupRunStatus(`Queued ${action.replace('_', ' ')} run: ${data.run_id || data.launch?.runId || 'started'}`);
    } catch (error: any) {
      setGroupRunStatus(error?.message || 'Group workflow failed');
    }
  };

  const pageMeta = useMemo(() => {
    if (activeTab === 'listeners') {
      return {
        title: 'Social Media Listener',
        subtitle: 'Monitor keywords, scrape posts and comments, and schedule recurring listener runs.',
      };
    }
    if (activeTab === 'keywords') {
      return {
        title: 'Trending Keyword Analysis',
        subtitle: 'Find trend opportunities across Google, Instagram, Facebook, and LinkedIn viral topics.',
      };
    }
    if (activeTab === 'groups') {
      return {
        title: 'Group Scraping & Engagement',
        subtitle: 'Run group listening, comment scraping, member scraping, posting, and commenting workflows.',
      };
    }
    return {
      title: 'Social Media Advertising Scrape',
      subtitle: 'Competitor ad discovery across Google, Meta, and LinkedIn.',
    };
  }, [activeTab]);

  const startAdRun = async (input: {
    source: 'google' | 'facebook' | 'linkedin_ads';
    company_name?: string;
    keyword?: string;
    start_url?: string;
    max_results?: number;
  }) => {
    if (!userEmail) throw new Error('Missing user');
    const path =
      input.source === 'google'
        ? '/scrape/google-ads'
        : input.source === 'linkedin_ads'
          ? '/scrape/linkedin-ads'
          : '/scrape/meta-ads';
    const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userEmail,
        company_name: input.company_name,
        keyword: input.keyword,
        start_url: input.start_url,
        max_results: input.max_results ?? 10,
      }),
    });
    const data = await res.json();
    if (!data.success || !data.run_id) throw new Error(data.error || 'Ad search failed');
    return data as { run_id: string; dataset_id?: string };
  };

  const startListenerRun = async (input: {
    source: 'instagram_posts' | 'instagram_reels' | 'linkedin_listeners' | 'facebook_listeners' | 'tiktok_listeners' | 'x_listeners';
    term: string;
    mode: ListenerMode;
    tiktokMode?: TikTokMode;
    tiktokProfiles?: string[];
    tiktokPostURLs?: string[];
    tiktokSearchQueries?: string[];
  }) => {
    if (!userEmail) throw new Error('Missing user');
    const isInstagram = input.source === 'instagram_posts' || input.source === 'instagram_reels';
    const isFacebook = input.source === 'facebook_listeners';
    const isTikTok = input.source === 'tiktok_listeners';
    const isX = input.source === 'x_listeners';
    const payload = isInstagram
      ? {
          userEmail,
          platform: 'instagram',
          mode: input.mode,
          term: input.term,
          resultsType: input.source === 'instagram_reels' ? 'reels' : 'posts',
          resultsLimit: 100,
        }
      : isFacebook
        ? {
            userEmail,
            platform: 'facebook',
            mode: 'keyword',
            term: input.term,
            query: input.term,
            location: facebookListenerLocation.trim() || undefined,
            searchType: 'top',
            resultsCount: 100,
          }
      : isTikTok
        ? {
            userEmail,
            platform: 'tiktok',
            mode: input.mode,
            term: input.term,
            hashtags:
              input.tiktokMode === 'terms' && input.mode === 'hashtag'
                ? [input.term.replace(/^#+/, '')]
                : undefined,
            profiles: input.tiktokMode === 'profiles' ? (input.tiktokProfiles || []) : undefined,
            postURLs: input.tiktokMode === 'post_urls' ? (input.tiktokPostURLs || []) : undefined,
            searchQueries: input.tiktokMode === 'terms' ? (input.tiktokSearchQueries || [input.term]) : undefined,
            resultsPerPage: 100,
            profileScrapeSections: ['videos'],
            profileSorting: 'latest',
            excludePinnedPosts: false,
          }
      : isX
        ? {
            userEmail,
            platform: 'x',
            mode: input.mode,
            term: input.term,
            searchTerms: input.tiktokSearchQueries?.length ? input.tiktokSearchQueries : [input.term],
            twitterHandles: input.tiktokProfiles || [],
            startUrls: input.tiktokPostURLs || [],
            maxItems: 1000,
            sort: 'Latest',
            tweetLanguage: 'en',
          }
      : {
          userEmail,
          platform: 'linkedin',
          mode: 'keyword',
          term: input.term,
          sort_type: 'date_posted',
          page_number: 1,
          date_filter: 'past-week',
          limit: 50,
        };
    const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!data.success || !data.run_id) throw new Error(data.error || 'Social listener search failed');
    return data as { run_id: string; dataset_id?: string };
  };

  const startTrendingRun = async (input: {
    source: 'google_trending' | 'instagram_trending' | 'facebook_trending' | 'linkedin_viral' | 'tiktok_trending' | 'x_trending';
    term: string;
    maxResults?: number;
    startPage?: number;
    searchQueries?: string[];
    profiles?: string[];
    postURLs?: string[];
    hashtags?: string[];
    oldestPostDateUnified?: string;
    leastDiggs?: number;
  }) => {
    if (!userEmail) throw new Error('Missing user');
    const maxResults = Math.max(1, Math.min(150, Number(input.maxResults ?? 150)));
    const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/trending-keywords`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userEmail,
        source: input.source,
        term: input.term,
        maxResults,
        startPage: input.startPage ?? 1,
        searchQueries: Array.isArray(input.searchQueries) && input.searchQueries.length ? input.searchQueries : [input.term],
        profiles: input.profiles,
        postURLs: input.postURLs,
        hashtags: input.hashtags,
        oldestPostDateUnified: input.oldestPostDateUnified,
        leastDiggs: input.leastDiggs,
      }),
    });
    const data = await res.json();
    if (!data.success || !data.run_id) throw new Error(data.error || 'Trending scrape failed');
    return data as { run_id: string; dataset_id?: string };
  };

  const openListenerScheduleModal = () => {
    if (!activeRun) return;
    setListenerScheduleModal({
      open: true,
      name: `${activeRun.term} ${activeRun.source.replace(/_/g, ' ')}`.trim(),
      days: [],
      hour: 9,
      autoCommentEnabled: false,
      autoCommentText: '',
      autoCommentMaxPosts: 5,
      saving: false,
      error: '',
    });
  };

  const toggleListenerScheduleDay = (day: string) => {
    setListenerScheduleModal((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
    }));
  };

  const createListenerSchedule = async () => {
    if (!userEmail || !activeRun) return;
    if (!listenerScheduleModal.name.trim()) {
      setListenerScheduleModal((prev) => ({ ...prev, error: 'Enter a schedule name.' }));
      return;
    }
    if (listenerScheduleModal.days.length === 0) {
      setListenerScheduleModal((prev) => ({ ...prev, error: 'Pick at least one day.' }));
      return;
    }

    const isInstagram = activeRun.source === 'instagram_posts' || activeRun.source === 'instagram_reels';
    const isFacebook = activeRun.source === 'facebook_listeners';
    const isTikTok = activeRun.source === 'tiktok_listeners';
    const isX = activeRun.source === 'x_listeners';
    const isLinkedin = activeRun.source === 'linkedin_listeners';
    const supportsAutoComment = isInstagram || isLinkedin;
    const autoCommentConfig =
      supportsAutoComment && listenerScheduleModal.autoCommentEnabled && listenerScheduleModal.autoCommentText.trim()
        ? {
            autoCommentEnabled: true,
            autoCommentText: listenerScheduleModal.autoCommentText.trim(),
            autoCommentMaxPosts: Math.max(1, Math.min(25, listenerScheduleModal.autoCommentMaxPosts || 5)),
          }
        : {};
    const payload = isInstagram
      ? {
          userEmail,
          name: listenerScheduleModal.name.trim(),
          scheduleDays: listenerScheduleModal.days,
          scheduleHour: listenerScheduleModal.hour,
          source: activeRun.source,
          platform: 'instagram',
          mode: activeRun.mode ?? listenerMode,
          term: activeRun.term,
          resultsType: activeRun.source === 'instagram_reels' ? 'reels' : 'posts',
          resultsLimit: 100,
          ...autoCommentConfig,
        }
      : isFacebook
        ? {
            userEmail,
            name: listenerScheduleModal.name.trim(),
            scheduleDays: listenerScheduleModal.days,
            scheduleHour: listenerScheduleModal.hour,
            source: activeRun.source,
            platform: 'facebook',
            mode: 'keyword',
            term: activeRun.term,
            query: activeRun.term,
            location: facebookListenerLocation.trim() || undefined,
            searchType: 'top',
            resultsCount: 100,
          }
        : isTikTok
          ? {
              userEmail,
              name: listenerScheduleModal.name.trim(),
              scheduleDays: listenerScheduleModal.days,
              scheduleHour: listenerScheduleModal.hour,
              source: activeRun.source,
              platform: 'tiktok',
              mode: activeRun.mode ?? listenerMode,
              term: activeRun.term,
              hashtags: (activeRun.mode ?? listenerMode) === 'hashtag' ? [activeRun.term.replace(/^#+/, '')] : undefined,
              resultsPerPage: 100,
              profileScrapeSections: ['videos'],
              profileSorting: 'latest',
              excludePinnedPosts: false,
            }
        : isX
          ? {
              userEmail,
              name: listenerScheduleModal.name.trim(),
              scheduleDays: listenerScheduleModal.days,
              scheduleHour: listenerScheduleModal.hour,
              source: activeRun.source,
              platform: 'x',
              mode: activeRun.mode ?? listenerMode,
              term: activeRun.term,
              searchTerms: [activeRun.term],
              maxItems: 1000,
              sort: 'Latest',
              tweetLanguage: 'en',
            }
        : {
            userEmail,
            name: listenerScheduleModal.name.trim(),
            scheduleDays: listenerScheduleModal.days,
            scheduleHour: listenerScheduleModal.hour,
            source: activeRun.source,
            platform: 'linkedin',
            mode: 'keyword',
            term: activeRun.term,
            sort_type: 'date_posted',
            page_number: 1,
            date_filter: 'past-week',
            limit: 50,
            ...autoCommentConfig,
          };

    setListenerScheduleModal((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to create listener schedule');
      setListenerScheduleModal({
        open: false,
        name: '',
        days: [],
        hour: 9,
        autoCommentEnabled: false,
        autoCommentText: '',
        autoCommentMaxPosts: 5,
        saving: false,
        error: '',
      });
      await loadListenerSchedules();
      alert(`Scheduled listener "${payload.name}" created.`);
    } catch (error) {
      setListenerScheduleModal((prev) => ({
        ...prev,
        saving: false,
        error: error instanceof Error ? error.message : 'Failed to create listener schedule',
      }));
    }
  };

  const pollAdRun = async (
    runId: string,
    meta: { company_name?: string; source: 'google' | 'facebook' | 'linkedin_ads' | 'social_listeners'; keyword?: string }
  ) => {
    let status = 'RUNNING';
    let datasetId: string | undefined;
    const started = Date.now();
    const maxStatusMs = 180_000;

    while (status === 'RUNNING' && Date.now() - started < maxStatusMs) {
      await new Promise((r) => setTimeout(r, 3000));
      const sres = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/check_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ run_id: runId }),
      });
      const sdata = await sres.json();
      status = sdata.status ?? 'RUNNING';
      datasetId = sdata.dataset_id ?? datasetId;
      if (status === 'FAILED') throw new Error('Ad data failed');
      if (status === 'SUCCEEDED') break;
    }

    const resultsStart = Date.now();
    const maxResultsMs = 120_000;
    while (Date.now() - resultsStart < maxResultsMs) {
      await new Promise((r) => setTimeout(r, 2000));
      const rres = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/get_results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          run_id: runId,
          dataset_id: datasetId,
          company_name: meta.company_name,
          source: meta.source === 'linkedin_ads' ? 'linkedin' : meta.source,
          keyword: meta.keyword,
        }),
      });
      const rdata = await rres.json();
      const rstatus = rdata?.status;
      if (rstatus === 'FAILED') throw new Error('Ad data failed');
      if (rstatus === 'RUNNING') continue;
      if (rdata?.success && Array.isArray(rdata.results)) {
        return rdata as { results: any[] };
      }
    }
    return { results: [] };
  };

  const pollForResults = async (runId: string, datasetId?: string): Promise<unknown[] | null> => {
    const maxAttempts = 90;
    const delayMs = 3000;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const statusRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/check_status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ run_id: runId, dataset_id: datasetId }),
        });
        const statusData = await statusRes.json();
        if (statusData.status === 'SUCCEEDED') {
          const resultsRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/get_results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ run_id: runId, dataset_id: datasetId || statusData.dataset_id }),
          });
          const resultsData = await resultsRes.json();
          return Array.isArray(resultsData.results) ? resultsData.results : [];
        }
        if ((statusData.status === 'FAILED' || statusData.status === 'ABORTED') && (datasetId || statusData.dataset_id)) {
          const resultsRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/get_results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ run_id: runId, dataset_id: datasetId || statusData.dataset_id }),
          });
          const resultsData = await resultsRes.json();
          if (Array.isArray(resultsData.results) && resultsData.results.length > 0) {
            return resultsData.results;
          }
        }
        if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
          throw new Error('Scrape failed');
        }
      } catch {
        // continue polling
      }
    }
    return null;
  };

  const summarizeWebsiteData = (data: unknown): {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    logoUrl?: string;
    domains?: string[];
    phones?: string[];
    address?: string;
    socials?: Record<string, string[]>;
    keywords?: string[];
    sample?: string;
    pagesCount: number;
  } => {
    const collectPages = (value: unknown): any[] => {
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== 'object') return [];
      const obj = value as Record<string, unknown>;
      const directKeys = ['pages', 'results', 'items'];
      for (const key of directKeys) {
        if (Array.isArray(obj[key])) return obj[key] as any[];
      }
      if (Array.isArray(obj.data)) return obj.data as any[];
      if (obj.data && typeof obj.data === 'object') {
        const dataObj = obj.data as Record<string, unknown>;
        for (const key of directKeys) {
          if (Array.isArray(dataObj[key])) return dataObj[key] as any[];
        }
      }
      if (Array.isArray(obj.payload)) return obj.payload as any[];
      if (obj.payload && typeof obj.payload === 'object') {
        const payloadObj = obj.payload as Record<string, unknown>;
        for (const key of directKeys) {
          if (Array.isArray(payloadObj[key])) return payloadObj[key] as any[];
        }
      }
      return [obj];
    };

    const pages = collectPages(data);
    const first = pages[0] as any;
    const meta = first?.metadata || {};
    const jsonLd = Array.isArray(meta?.jsonLd) ? meta.jsonLd : [];
    const graph = jsonLd
      .flatMap((node: any) => (node && node['@graph'] ? node['@graph'] : node))
      .filter(Boolean);
    const org =
      graph.find(
        (n: any) =>
          n && (n['@type'] === 'Organization' || n['@type'] === 'Corporation')
      ) || {};
    const canonicalUrl = meta?.canonicalUrl || org?.url || first?.url || '';
    const description = meta?.description || org?.description || '';
    const title = meta?.title || org?.name || '';
    const logoUrl =
      (typeof org?.logo === 'string' ? org.logo : org?.logo?.url) ||
      (typeof meta?.openGraph?.image === 'string' ? meta.openGraph.image : '') ||
      (typeof meta?.ogImage === 'string' ? meta.ogImage : '') ||
      (typeof meta?.favicon === 'string' ? meta.favicon : '') ||
      '';
    const address = org?.address
      ? [
          org.address?.streetAddress,
          org.address?.addressLocality,
          org.address?.addressRegion,
          org.address?.postalCode,
          org.address?.addressCountry,
        ]
          .filter(Boolean)
          .join(', ')
      : '';
    const contactPoints = Array.isArray(org?.contactPoint)
      ? org.contactPoint
      : [];
    const phones = contactPoints
      .flatMap((cp: any) =>
        Array.isArray(cp?.telephone)
          ? cp.telephone
          : cp?.telephone
            ? [cp.telephone]
            : []
      )
      .filter(Boolean);
    const sameAs = Array.isArray(org?.sameAs) ? org.sameAs : [];
    const socials: Record<string, string[]> = {
      linkedin: [],
      facebook: [],
      instagram: [],
      tiktok: [],
      youtube: [],
      x: [],
      podcast: [],
    };
    const socialUrlCandidates = new Set<string>();
    const normalizeUrl = (value: string) => {
      const trimmed = value.trim().replace(/[),.;]+$/g, '');
      if (!trimmed) return '';
      return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    };
    const addUrl = (value: unknown) => {
      if (typeof value !== 'string') return;
      const normalized = normalizeUrl(value);
      if (normalized) socialUrlCandidates.add(normalized);
    };
    const walk = (value: unknown) => {
      if (typeof value === 'string') {
        const decoded = value.replace(/\\\//g, '/');
        const httpMatches = decoded.match(/https?:\/\/[^\s"'<>]+/gi) || [];
        const bareMatches =
          decoded.match(
            /\b(?:www\.)?(?:linkedin\.com|facebook\.com|instagram\.com|tiktok\.com|youtube\.com|youtu\.be|x\.com|twitter\.com|podcasts\.apple\.com|open\.spotify\.com|podbean\.com|anchor\.fm)\/[^\s"'<>]+/gi
          ) || [];
        [...httpMatches, ...bareMatches].forEach(addUrl);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(walk);
        return;
      }
      if (value && typeof value === 'object') {
        Object.values(value as Record<string, unknown>).forEach(walk);
      }
    };

    sameAs.forEach(addUrl);
    for (const node of graph) {
      const nodeSameAs = Array.isArray((node as any)?.sameAs) ? (node as any).sameAs : [];
      nodeSameAs.forEach(addUrl);
      const brand = (node as any)?.brand;
      if (brand && typeof brand === 'object') {
        const brandSameAs = Array.isArray((brand as any)?.sameAs) ? (brand as any).sameAs : [];
        brandSameAs.forEach(addUrl);
      }
    }
    pages.forEach((page) => {
      walk(page);
      const pageMeta = (page as any)?.metadata || {};
      walk(pageMeta?.links);
      walk((page as any)?.links);
      walk((page as any)?.socials);
      // Last-resort extraction for deeply nested or escaped JSON-LD fields.
      try {
        walk(JSON.stringify(page));
      } catch {
        // ignore serialization errors
      }
    });

    const addIfMissing = (bucket: string[], url: string) => {
      if (!bucket.includes(url)) bucket.push(url);
    };
    for (const url of socialUrlCandidates) {
      const lower = url.toLowerCase();
      if (lower.includes('linkedin.com')) addIfMissing(socials.linkedin, url);
      else if (lower.includes('facebook.com')) addIfMissing(socials.facebook, url);
      else if (lower.includes('instagram.com')) addIfMissing(socials.instagram, url);
      else if (lower.includes('tiktok.com')) addIfMissing(socials.tiktok, url);
      else if (lower.includes('youtube.com') || lower.includes('youtu.be')) addIfMissing(socials.youtube, url);
      else if (lower.includes('x.com') || lower.includes('twitter.com')) addIfMissing(socials.x, url);
      else if (
        lower.includes('podcast') ||
        lower.includes('podbean') ||
        lower.includes('spotify') ||
        lower.includes('apple.com') ||
        lower.includes('anchor.fm')
      ) {
        addIfMissing(socials.podcast, url);
      }
    }
    const keywords = Array.isArray(meta?.keywords)
      ? meta.keywords
      : typeof meta?.keywords === 'string'
        ? meta.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
        : [];
    const sample = typeof first?.text === 'string' ? first.text.slice(0, 420) : '';
    const domains = canonicalUrl ? [canonicalUrl] : [];
    return {
      title,
      description,
      canonicalUrl,
      logoUrl: logoUrl || undefined,
      domains,
      phones,
      address,
      socials,
      keywords,
      sample,
      pagesCount: pages.length,
    };
  };

  const summarizeEcommerceData = (data: unknown): {
    total: number;
    withPrice: number;
    minPrice?: number;
    maxPrice?: number;
    avgPrice?: number;
    domains: string[];
    samples: Array<{
      title: string;
      price?: string;
      url?: string;
      image?: string;
      rating?: string;
      reviews?: string;
      availability?: string;
    }>;
  } => {
    const rawItems = Array.isArray(data)
      ? data
      : Array.isArray((data as { items?: unknown[] })?.items)
        ? (data as { items?: unknown[] }).items ?? []
        : [];
    const items = rawItems.map((entry: any) => (entry && typeof entry === 'object' && entry.items ? entry.items : entry));
    const pick = (obj: any, keys: string[]) => {
      for (const key of keys) {
        const val = obj?.[key];
        if (val !== undefined && val !== null && val !== '') return val;
      }
      return undefined;
    };
    const toStr = (val: any) =>
      typeof val === 'string' ? val : typeof val === 'number' ? String(val) : '';
    const toNum = (val: any) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^0-9.]+/g, '');
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : undefined;
      }
      return undefined;
    };
    const prices: number[] = [];
    const domains = new Set<string>();
    const samples: Array<{
      title: string;
      price?: string;
      url?: string;
      image?: string;
      rating?: string;
      reviews?: string;
      availability?: string;
    }> = [];
    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, any>;
      const title =
        toStr(pick(item, ['product_title', 'title', 'name', 'productTitle', 'productName'])) ||
        'Untitled item';
      const url = toStr(pick(item, ['url', 'productUrl', 'itemUrl', 'detailUrl', 'detail_url']));
      const image = toStr(pick(item, ['image_url', 'image', 'imageUrl', 'thumbnail', 'thumb', 'img']));
      const priceVal = pick(item, ['product_price', 'price', 'priceValue', 'salePrice', 'sale_price', 'price_amount']);
      const priceNum = toNum(priceVal);
      if (priceNum !== undefined) prices.push(priceNum);
      const price =
        priceNum !== undefined
          ? priceNum.toFixed(2)
          : toStr(priceVal);
      const rating = toStr(pick(item, ['ratings', 'rating', 'ratingValue', 'reviewRating']));
      const reviews = toStr(pick(item, ['reviewCount', 'reviews', 'ratingsCount']));
      const availability = toStr(pick(item, ['availability', 'stockStatus', 'inStock']));
      if (url) {
        try {
          const u = new URL(url);
          domains.add(u.hostname.replace(/^www\./, ''));
        } catch {
          // ignore
        }
      }
      if (samples.length < 6) {
        samples.push({ title, price, url, image, rating, reviews, availability });
      }
    }
    const total = items.length;
    const withPrice = prices.length;
    const minPrice = prices.length ? Math.min(...prices) : undefined;
    const maxPrice = prices.length ? Math.max(...prices) : undefined;
    const avgPrice = prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : undefined;
    return {
      total,
      withPrice,
      minPrice,
      maxPrice,
      avgPrice,
      domains: Array.from(domains),
      samples,
    };
  };
  useEffect(() => {
    setShowRawDrawer(false);
  }, [dataDrawer?.companyId, dataDrawer?.type]);
  useEffect(() => {
    if (!userEmail) return;
    const stored = loadStoredKeywords(userEmail);
    const normalized = stored.map((run) => {
      const source = (run as any).source as KeywordSource | undefined;
      const mode = (run as any).mode as ListenerMode | undefined;
      const resolvedSource: KeywordSource = source || 'facebook';
      return {
        ...run,
        source: resolvedSource,
        mode,
        key: (run as any).key || buildKeywordRunKey(run.term, resolvedSource, mode),
      } as KeywordRun;
    });
    setKeywordRuns(normalized);
  }, [userEmail]);

  useEffect(() => {
    try {
      localStorage.setItem(KEYWORD_HISTORY_VISIBILITY_KEY, String(showKeywordHistory));
    } catch {
      // ignore
    }
  }, [showKeywordHistory]);

  useEffect(() => {
    if (!userEmail) return;
    if (activeTab !== 'listeners') return;
    loadListenerCatalog().catch(() => undefined);
    loadListenerSchedules().catch(() => undefined);
  }, [userEmail, activeTab]);

  useEffect(() => {
    if (!userEmail) return;
    const controller = new AbortController();
    setLoadingCompanies(true);
    loadCompanies(userEmail, { signal: controller.signal })
      .then((rows) => {
        if (controller.signal.aborted) return;
        setCompanies(
          rows.map((c) => ({
            companyId: c.companyId,
            companyName: c.companyName,
            website: c.website || '',
            facebook: (c as any).facebook || '',
            linkedin: (c as any).linkedin || '',
          }))
        );
      })
      .catch((error) => {
        if ((error as Error)?.name !== 'AbortError') {
          console.error('Failed to load companies:', error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCompanies(false);
      });
    return () => controller.abort();
  }, [userEmail]);

  const filteredCompanies = useMemo(() => {
    if (!searchTerm) return companies;
    const q = searchTerm.toLowerCase();
    return companies.filter((c) => c.companyName.toLowerCase().includes(q));
  }, [companies, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / perPage));
  const startIdx = (currentPage - 1) * perPage;
  const endIdx = Math.min(startIdx + perPage, filteredCompanies.length);
  const paginatedCompanies = filteredCompanies.slice(startIdx, endIdx);

  const resolveWebsite = async (companyName: string) => {
    if (!userEmail) return '';
    const res = await fetch(`${API_CONFIG.RESEARCH_API}/ad-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'resolve_website',
        account_id: userEmail,
        company_name: companyName,
      }),
    });
    const data = await res.json();
    return String(data?.website_url ?? '').trim();
  };

  const handleScrapeWebsite = async (company: CompanyRow) => {
    if (!userEmail) return;
    let websiteUrl = normalizeWebsiteUrl(company.website || '');
    if (!websiteUrl) {
      websiteUrl = normalizeWebsiteUrl(await resolveWebsite(company.companyName));
    }
    if (!websiteUrl) return;

    setScrapeLoading((prev) => ({ ...prev, [company.companyId]: { ...(prev[company.companyId] || {}), website: true } }));
    enqueueScrapeJob(
      { source: 'website', companyName: company.companyName, label: `${company.companyName} • website` },
      async (update) => {
        try {
          update(10, 'Starting website scrape…');
          const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/website`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              website_url: websiteUrl,
              company_name: company.companyName,
              userEmail,
              force_refresh: false,
            }),
          });
          const data = await res.json();
          if (!data.success || !data.content?.run_id) throw new Error(data.error || 'Website scrape failed');
          update(35, 'Crawling website…');
          const results = await pollForResults(data.content.run_id, data.content.dataset_id);
          if (results && Array.isArray(results) && results.length > 0) {
            setSiteCache((prev) => ({
              ...prev,
              [company.companyId]: { ...(prev[company.companyId] || {}), website: { data: results, scrapedAt: new Date().toISOString() } },
            }));
            setDataDrawer({ companyId: company.companyId, companyName: company.companyName, type: 'website' });
            update(100, 'Complete');
          } else {
            update(100, 'Complete (no data)');
          }
        } finally {
          setScrapeLoading((prev) => ({ ...prev, [company.companyId]: { ...(prev[company.companyId] || {}), website: false } }));
        }
      }
    );
  };

  const runCompanyAds = async (company: CompanyRow, source: 'google' | 'facebook' | 'linkedin', update?: (p: number, m: string) => void) => {
    if (!userEmail) return;
    const start = await startAdRun({
      source: source === 'linkedin' ? 'linkedin_ads' : source,
      company_name: company.companyName,
      start_url: source === 'linkedin'
        ? `https://www.linkedin.com/ad-library/search?accountOwner=${encodeURIComponent(extractLinkedinOwner(company.linkedin || company.companyName))}&countries=US&dateOption=last-30-days`
        : undefined,
      max_results: 10,
      keyword: source === 'facebook' ? company.companyName : undefined,
    });
    update?.(30, 'Polling results…');
    const data = await pollAdRun(start.run_id, {
      company_name: company.companyName,
      source: source === 'linkedin' ? 'linkedin_ads' : source,
      keyword: source === 'facebook' ? company.companyName : undefined,
    });
    const results = Array.isArray(data?.results) ? data.results : [];
    setAdCache((prev) => ({
      ...prev,
      [company.companyId]: { ...(prev[company.companyId] || {}), [source]: { results, updatedAt: new Date().toISOString() } },
    }));
    setAdDrawer({ open: true, companyName: company.companyName, source, data: { results } });
  };

  const openAdDrawer = async (company: CompanyRow, source: 'google' | 'facebook' | 'linkedin') => {
    const cached = adCache[company.companyId]?.[source];
    if (cached?.results?.length) {
      setAdDrawer({ open: true, companyName: company.companyName, source, data: cached });
      return;
    }
    setAdLoading((prev) => ({ ...prev, [company.companyId]: { ...(prev[company.companyId] || {}), [source]: true } }));
    enqueueScrapeJob(
      { source: 'ad_company', companyName: company.companyName, label: `Ads - ${company.companyName} (${source})` },
      async (update) => {
        try {
          setAdStatus((prev) => ({ ...prev, [company.companyId]: `Running ${source} ads…` }));
          update(10, 'Starting ad search…');
          await runCompanyAds(company, source, update);
          update(100, 'Complete');
          setAdStatus((prev) => ({ ...prev, [company.companyId]: `Complete (${source})` }));
        } finally {
          setAdLoading((prev) => ({ ...prev, [company.companyId]: { ...(prev[company.companyId] || {}), [source]: false } }));
        }
      }
    );
  };

  const handleAddKeyword = () => {
    const term = keywordInput.trim();
    if (!term) return;
    const modeForRun = activeTab === 'listeners' ? listenerMode : undefined;
    const runKey = buildKeywordRunKey(term, keywordSource, modeForRun);
    if (keywordRuns.some((k) => k.key === runKey)) {
      setKeywordInput('');
      return;
    }
    const nextRun: KeywordRun = {
      key: runKey,
      term,
      source: keywordSource,
      mode: modeForRun,
      status: 'idle',
      results: [],
    };
    const next: KeywordRun[] = [nextRun, ...keywordRuns];
    setKeywordRuns(next);
    setActiveKeyword(runKey);
    setKeywordInput('');
    if (userEmail) saveStoredKeywords(userEmail, next);
  };

  const runKeyword = (run: KeywordRun) => {
    if (!userEmail) return;
    const term = run.term;
    const source = run.source;
    const mode = run.mode || (activeTab === 'listeners' ? listenerMode : undefined);
    setKeywordRuns((prev) => prev.map((k) => (k.key === run.key ? { ...k, status: 'running', error: undefined } : k)));

    enqueueScrapeJob(
      { source: 'ad_keyword', companyName: term, label: `Ad Keyword - ${term}` },
      async (update) => {
        try {
          const isListenerSource =
            source === 'instagram_posts' ||
            source === 'instagram_reels' ||
            source === 'linkedin_listeners' ||
            source === 'facebook_listeners' ||
            source === 'tiktok_listeners' ||
            source === 'x_listeners';
          const isTrendingSource =
            source === 'google_trending' ||
            source === 'instagram_trending' ||
            source === 'facebook_trending' ||
            source === 'linkedin_viral' ||
            source === 'tiktok_trending' ||
            source === 'x_trending';

          update(
            10,
            isListenerSource
              ? 'Preparing social listener search…'
              : isTrendingSource
                ? 'Preparing trending keyword scrape…'
                : 'Preparing keyword ad search…'
          );
          let start: { run_id: string; dataset_id?: string } | null = null;
          let results: Array<Record<string, unknown>> = [];
          if (isListenerSource) {
            const tiktokProfiles = parseDelimitedList(tiktokProfilesInput);
            const tiktokPostURLs = parseDelimitedList(tiktokPostUrlsInput);
            const tiktokSearchQueries = parseDelimitedList(tiktokSearchQueriesInput);
            start = await startListenerRun({
              source: source as 'instagram_posts' | 'instagram_reels' | 'linkedin_listeners' | 'facebook_listeners' | 'tiktok_listeners' | 'x_listeners',
              term,
              mode: mode || 'keyword',
              tiktokMode,
              tiktokProfiles,
              tiktokPostURLs,
              tiktokSearchQueries,
            });
            update(55, 'Polling results…');
            const data = await pollAdRun(start.run_id, {
              company_name: companies.find((c) => c.companyId === keywordCompanyId)?.companyName,
              source: 'social_listeners',
              keyword: term,
            });
            update(90, 'Processing results…');
            results = Array.isArray(data?.results) ? (data.results as Array<Record<string, unknown>>) : [];
          } else if (isTrendingSource && source === 'linkedin_viral') {
            const perPage = 20;
            const targetPosts = Math.max(20, Math.min(500, Number(linkedinViralTargetPosts || 100)));
            const pages = Math.max(1, Math.min(10, Math.ceil(targetPosts / perPage)));
            const merged: Array<Record<string, unknown>> = [];
            for (let page = 1; page <= pages; page += 1) {
              update(10 + Math.floor((page - 1) * (45 / pages)), `Starting LinkedIn viral page ${page}/${pages}…`);
              const pStart = await startTrendingRun({
                source: 'linkedin_viral',
                term,
                maxResults: perPage,
                startPage: page,
                searchQueries: [term],
              });
              if (!start) start = pStart;
              update(55 + Math.floor((page - 1) * (35 / pages)), `Polling LinkedIn viral page ${page}/${pages}…`);
              const pData = await pollAdRun(pStart.run_id, {
                source: 'social_listeners',
                keyword: `${term}::page${page}`,
              });
              const pageResults = Array.isArray(pData?.results) ? (pData.results as Array<Record<string, unknown>>) : [];
              merged.push(...pageResults);
            }
            const deduped = new Map<string, Record<string, unknown>>();
            for (const item of merged) {
              const key =
                String(
                  item.postUrl ??
                    item.post_url ??
                    item.url ??
                    item.full_urn ??
                    item.activity_id ??
                    item.id ??
                    ''
                ) || JSON.stringify(item);
              if (!deduped.has(key)) deduped.set(key, item);
            }
            update(90, 'Processing results…');
            results = Array.from(deduped.values()).slice(0, targetPosts);
          } else if (isTrendingSource) {
            const tiktokProfiles = parseDelimitedList(tiktokProfilesInput);
            const tiktokPostURLs = parseDelimitedList(tiktokPostUrlsInput);
            const tiktokSearchQueries = parseDelimitedList(tiktokSearchQueriesInput);
            start = await startTrendingRun({
              source: source as 'google_trending' | 'instagram_trending' | 'facebook_trending' | 'linkedin_viral' | 'tiktok_trending' | 'x_trending',
              term,
              searchQueries: source === 'tiktok_trending' ? (tiktokSearchQueries.length ? tiktokSearchQueries : [term]) : undefined,
              profiles: source === 'tiktok_trending' ? tiktokProfiles : undefined,
              postURLs: source === 'tiktok_trending' ? tiktokPostURLs : undefined,
              hashtags: source === 'tiktok_trending' && mode === 'hashtag' ? [term.replace(/^#+/, '')] : undefined,
            });
            update(55, 'Polling results…');
            const data = await pollAdRun(start.run_id, {
              source: 'social_listeners',
              keyword: term,
            });
            update(90, 'Processing results…');
            const rawResults = Array.isArray(data?.results) ? (data.results as Array<Record<string, unknown>>) : [];
            results = rawResults.slice(0, 150);
          } else {
            start = await startAdRun({
              source: source as 'google' | 'facebook' | 'linkedin_ads',
              keyword: term,
              company_name: companies.find((c) => c.companyId === keywordCompanyId)?.companyName,
              start_url:
                source === 'linkedin_ads' && linkedinOwner
                  ? `https://www.linkedin.com/ad-library/search?accountOwner=${encodeURIComponent(linkedinOwner)}&countries=US&dateOption=last-30-days`
                  : undefined,
              max_results: 10,
            });
            update(55, 'Polling ads…');
            const data = await pollAdRun(start.run_id, {
              company_name: companies.find((c) => c.companyId === keywordCompanyId)?.companyName,
              source: source as 'google' | 'facebook' | 'linkedin_ads',
              keyword: term,
            });
            update(90, 'Processing results…');
            results = Array.isArray(data?.results) ? (data.results as Array<Record<string, unknown>>) : [];
          }
          setKeywordRuns((prev) =>
            prev.map((k) => (k.key === run.key ? { ...k, status: 'succeeded' as const, results } : k))
          );
          if ((isListenerSource || isTrendingSource) && start) {
            await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/catalog/save`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userEmail,
                itemType: isTrendingSource ? 'trending_keyword_search' : 'listener_search',
                term,
                source,
                mode,
                runId: start.run_id,
                datasetId: start.dataset_id,
                results,
                meta: {
                  count: results.length,
                  maxResults: isTrendingSource ? 150 : undefined,
                  targetPosts: source === 'linkedin_viral' ? linkedinViralTargetPosts : undefined,
                },
              }),
            }).catch(() => undefined);
          }
          if (userEmail) {
            const next: KeywordRun[] = keywordRuns.map((k) => (k.key === run.key ? { ...k, status: 'succeeded' as const, results } : k));
            saveStoredKeywords(userEmail, next);
          }
        } catch (err: any) {
          setKeywordRuns((prev) =>
            prev.map((k) => (k.key === run.key ? { ...k, status: 'failed' as const, error: err?.message || 'Search failed' } : k))
          );
          throw err;
        }
      }
    );
  };

  const activeRun = useMemo(
    () => (activeKeyword ? keywordRuns.find((k) => k.key === activeKeyword) : undefined),
    [activeKeyword, keywordRuns]
  );

  const isListenerTab = activeTab === 'listeners';
  const visibleKeywordRuns = useMemo(() => {
    const listenerSources: KeywordSource[] = ['instagram_posts', 'instagram_reels', 'linkedin_listeners', 'facebook_listeners', 'tiktok_listeners', 'x_listeners'];
    const keywordToolSources: KeywordSource[] = ['linkedin_ads', 'google_trending', 'instagram_trending', 'facebook_trending', 'linkedin_viral', 'tiktok_trending', 'x_trending'];
    const allowed = isListenerTab ? listenerSources : keywordToolSources;
    return keywordRuns.filter((run) => allowed.includes(run.source) && run.source === keywordSource);
  }, [isListenerTab, keywordRuns, keywordSource]);

  useEffect(() => {
    if (!activeKeyword) return;
    if (visibleKeywordRuns.some((run) => run.key === activeKeyword)) return;
    setActiveKeyword(visibleKeywordRuns[0]?.key ?? null);
  }, [activeKeyword, visibleKeywordRuns]);

  const removeKeywordRun = (runKey: string) => {
    const next = keywordRuns.filter((run) => run.key !== runKey);
    setKeywordRuns(next);
    if (userEmail) saveStoredKeywords(userEmail, next);
    if (activeKeyword === runKey) setActiveKeyword(next[0]?.key ?? null);
  };

  const getListenerPlatform = (row: Record<string, unknown>): string => {
    const explicit = row.platform ?? row.source;
    if (explicit !== undefined && explicit !== null && explicit !== '') return String(explicit);
    const url = String(row.postUrl ?? row.post_url ?? row.url ?? '');
    if (url.includes('facebook.com')) return 'facebook';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('tiktok.com')) return 'tiktok';
    if (url.includes('x.com') || url.includes('twitter.com')) return 'x';
    if (url.includes('linkedin.com')) return 'linkedin';
    if (row.full_urn || row.activity_id || row.postUrl || row.post_url) return 'linkedin';
    const source = activeRun?.source;
    if (source === 'tiktok_listeners' || source === 'tiktok_trending') return 'tiktok';
    if (source === 'x_listeners' || source === 'x_trending') return 'x';
    if (source === 'facebook_listeners' || source === 'facebook_trending') return 'facebook';
    if (source === 'linkedin_listeners' || source === 'linkedin_viral') return 'linkedin';
    if (source === 'instagram_posts' || source === 'instagram_reels' || source === 'instagram_trending') return 'instagram';
    return 'social';
  };

  const getListenerAuthor = (row: Record<string, unknown>): string => {
    const author = row.author as Record<string, unknown> | undefined;
    const value =
      row.authorName ??
      row.author_name ??
      author?.name ??
      author?.username ??
      row.author_username ??
      row.username ??
      row.ownerUsername ??
      row.user;
    return value !== undefined && value !== null && String(value).trim() ? String(value) : '—';
  };

  const getListenerAuthorProfileUrl = (row: Record<string, unknown>): string => {
    const author = row.author as Record<string, unknown> | undefined;
    const direct =
      author?.url ??
      author?.profile_url ??
      row.author_profile_url ??
      row.authorProfileUrl;
    const directUrl = direct !== undefined && direct !== null ? String(direct).trim() : '';
    if (directUrl) return directUrl;
    const fbId = String(row.author_user_id ?? '').trim();
    if (fbId && /^\d+$/.test(fbId)) return `https://www.facebook.com/profile.php?id=${fbId}`;
    return '';
  };

  const getListenerText = (row: Record<string, unknown>): string => {
    const raw = row.postContent ?? row.post_content ?? row.text ?? row.caption ?? row.description ?? row.title;
    if (!raw) return '—';
    const txt = String(raw).replace(/\s+/g, ' ').trim();
    return txt.length > 220 ? `${txt.slice(0, 220)}…` : txt;
  };

  const getListenerPostedAt = (row: Record<string, unknown>): string => {
    const posted = row.posted_at as Record<string, unknown> | undefined;
    const timestamp = Number(row.postedAtTimestamp ?? row.posted_at_timestamp ?? row.timestamp ?? 0);
    if (Number.isFinite(timestamp) && timestamp > 0) {
      const ms = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
      return new Date(ms).toISOString();
    }
    const value = row.postedDate ?? row.postedAgo ?? posted?.date ?? posted?.display_text ?? row.createdAt;
    return value !== undefined && value !== null && String(value).trim() ? String(value) : '—';
  };

  const getListenerPostedTimestamp = (row: Record<string, unknown>): number => {
    const posted = (row.posted_at ?? {}) as Record<string, unknown>;
    const ts = Number(row.postedAtTimestamp ?? row.posted_at_timestamp ?? posted.timestamp ?? row.timestamp ?? 0);
    if (Number.isFinite(ts) && ts > 0) return ts;
    const parsed = Date.parse(String(row.postedDate ?? posted.date ?? row.createdAt ?? ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getListenerUrl = (row: Record<string, unknown>): string => {
    const value = row.postUrl ?? row.post_url ?? row.url;
    return value !== undefined && value !== null && String(value).trim() ? String(value) : '';
  };

  const normalizePostUrl = (url: string): string => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    try {
      const u = new URL(raw);
      // Keep only stable path for matching across variants with different params.
      const path = u.pathname.length > 1 ? u.pathname.replace(/\/+$/, '') : u.pathname;
      return `${u.origin}${path}`.toLowerCase();
    } catch {
      return raw.toLowerCase().split('?')[0].replace(/\/+$/, '');
    }
  };

  const extractInstagramShortCode = (url: string): string => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    const match = raw.match(/instagram\.com\/p\/([^/?#]+)/i) || raw.match(/instagram\.com\/reel\/([^/?#]+)/i);
    return match?.[1] ? match[1].toLowerCase() : '';
  };

  const extractLinkedInActivityId = (url: string): string => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    const match = raw.match(/activity-(\d+)/i);
    if (match?.[1]) return match[1];
    const urnMatch = raw.match(/ugcPost:(\d+)/i);
    if (urnMatch?.[1]) return urnMatch[1];
    return '';
  };

  const getListenerCommentCount = (row: Record<string, unknown>): number | null => {
    const stats = (row.stats ?? {}) as Record<string, unknown>;
    const candidate =
      row.commentCount ??
      row.comment_count ??
      stats.comments ??
      row.commentsCount ??
      row.num_comments ??
      row.comments_count;
    const n = Number(candidate);
    return Number.isFinite(n) ? n : null;
  };

  const normalizeLanguageCode = (value: unknown): string => {
    const raw = String(value ?? '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.startsWith('english')) return 'en';
    const twoLetter = raw.match(/^[a-z]{2}/)?.[0] || '';
    return twoLetter || raw;
  };

  const getRowLanguageCode = (row: Record<string, unknown>): string => {
    const candidate =
      row.language ??
      row.lang ??
      row.languageCode ??
      row.detectedLanguage ??
      row.detected_language ??
      row.postLanguage ??
      row.locale;
    return normalizeLanguageCode(candidate);
  };

  const extractCommentsFromDeepResults = (items: Array<Record<string, unknown>>): ListenerComment[] => {
    const out: ListenerComment[] = [];
    const seen = new Set<string>();
    const toArray = (value: unknown): Array<Record<string, unknown>> => {
      if (!value) return [];
      if (Array.isArray(value)) return value.filter((v): v is Record<string, unknown> => Boolean(v) && typeof v === 'object');
      if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        if (Array.isArray(obj.edges)) {
          return obj.edges
            .map((edge) => (edge && typeof edge === 'object' ? (edge as Record<string, unknown>).node : null))
            .filter((v): v is Record<string, unknown> => Boolean(v) && typeof v === 'object');
        }
      }
      return [];
    };
    const toComment = (comment: Record<string, unknown>, postUrl: string): ListenerComment => {
      const authorObj = (comment.author ?? comment.user ?? comment.commenter ?? {}) as Record<string, unknown>;
      const ownerObj = (comment.owner ?? {}) as Record<string, unknown>;
      const first = String(authorObj.firstName ?? '').trim();
      const last = String(authorObj.lastName ?? '').trim();
      const fullFromParts = `${first} ${last}`.trim();
      const authorNameRaw =
        authorObj.name ??
        (fullFromParts || undefined) ??
        comment.profileName ??
        comment.authorName ??
        comment.author_username ??
        comment.authorUsername ??
        comment.ownerUsername ??
        comment.username ??
        comment.ownerUsername ??
        ownerObj.username ??
        comment.userName ??
        comment.name ??
        '';
      const publicId =
        String(
          authorObj.publicId ??
          authorObj.profileId ??
          comment.authorPublicId ??
          ''
        ).trim();
      const profileRawBase =
        authorObj.profile_url ??
        authorObj.profileUrl ??
        authorObj.authorProfileUrl ??
        authorObj.url ??
        ownerObj.profile_url ??
        ownerObj.profileUrl ??
        comment.author_profile_url ??
        comment.authorProfileUrl ??
        comment.profile_url ??
        comment.profileUrl ??
        comment.userUrl ??
        comment.ownerProfileUrl ??
        '';
      const authorUserId = String(comment.author_user_id ?? '').trim();
      const igUsername = String(comment.username ?? comment.ownerUsername ?? authorObj.username ?? ownerObj.username ?? '').trim();
      const profileRaw =
        (profileRawBase ? String(profileRawBase) : '') ||
        (igUsername ? `https://www.instagram.com/${igUsername.replace(/^@+/, '')}/` : '') ||
        (authorUserId ? `https://www.facebook.com/profile.php?id=${authorUserId}` : '') ||
        (publicId ? `https://www.linkedin.com/in/${publicId}` : '');
      const textRaw =
        comment.text ??
        comment.comment ??
        comment.body ??
        comment.content ??
        comment.message ??
        comment.commentText ??
        comment.comment_text ??
        '';
      const timeMsRaw = Number(comment.time ?? comment.timestamp ?? comment.create_time ?? comment.created_time ?? 0);
      const timeMs = Number.isFinite(timeMsRaw) && timeMsRaw > 0 && timeMsRaw < 1_000_000_000_000 ? timeMsRaw * 1000 : timeMsRaw;
      const postedRaw =
        (Number.isFinite(timeMs) && timeMs > 0 ? new Date(timeMs).toISOString() : '') ||
        comment.date ||
        comment.posted_at ||
        comment.createdAt ||
        comment.created_at ||
        '';
      const idRaw = comment.id ?? comment.comment_id ?? comment.commentId ?? comment.pk ?? `${postUrl}:${authorNameRaw}:${textRaw}`;
      const usernameRaw =
        comment.author_username ??
        comment.authorUsername ??
        comment.ownerUsername ??
        comment.username ??
        authorObj.username ??
        ownerObj.username ??
        '';
      const profileIdRaw =
        authorObj.profileId ??
        authorObj.id ??
        comment.profileId ??
        comment.profile_id ??
        comment.author_user_id ??
        comment.ownerId ??
        comment.userId ??
        '';
      const profilePictureRaw =
        authorObj.profilePicture ??
        authorObj.profile_picture ??
        authorObj.profile_pic_url ??
        comment.profilePicture ??
        comment.profile_picture ??
        comment.ownerProfilePicUrl ??
        '';
      const locationRaw =
        authorObj.location ??
        comment.location ??
        comment.city ??
        comment.country ??
        '';
      const titleRaw =
        authorObj.headline ??
        authorObj.title ??
        comment.authorTitle ??
        comment.title ??
        comment.headline ??
        ownerObj.title ??
        '';
      const descriptionRaw =
        authorObj.description ??
        authorObj.about ??
        authorObj.bio ??
        comment.authorDescription ??
        comment.description ??
        comment.bio ??
        ownerObj.description ??
        '';
      const emailRaw =
        authorObj.email ??
        comment.email ??
        comment.authorEmail ??
        ownerObj.email ??
        extractEmailAddress(
          [
            authorObj.email,
            comment.email,
            comment.authorEmail,
            descriptionRaw,
            textRaw,
          ]
            .filter(Boolean)
            .join(' ')
        );
      const likesRaw =
        comment.likesCount ??
        comment.likes_count ??
        comment.like_count ??
        comment.likeCount ??
        null;
      const repliesRaw =
        comment.repliesCount ??
        comment.replies_count ??
        comment.reply_count ??
        comment.replyCount ??
        null;
      return {
        id: String(idRaw),
        postUrl,
        platform: String(postUrl || '').includes('facebook.com')
          ? 'facebook'
          : String(postUrl || '').includes('instagram.com')
            ? 'instagram'
            : String(postUrl || '').includes('linkedin.com')
              ? 'linkedin'
              : undefined,
        authorName: authorNameRaw ? String(authorNameRaw) : '—',
        authorUsername: usernameRaw ? String(usernameRaw) : undefined,
        authorProfileId: profileIdRaw ? String(profileIdRaw) : undefined,
        authorProfileUrl: profileRaw ? String(profileRaw) : '',
        authorProfilePicture: profilePictureRaw ? String(profilePictureRaw) : undefined,
        authorTitle: titleRaw ? String(titleRaw) : undefined,
        authorDescription: descriptionRaw ? String(descriptionRaw) : undefined,
        location: locationRaw ? String(locationRaw) : undefined,
        email: emailRaw ? String(emailRaw).trim() : undefined,
        text: textRaw ? String(textRaw) : '—',
        postedAt: postedRaw ? String(postedRaw) : '—',
        likesCount: Number.isFinite(Number(likesRaw)) ? Number(likesRaw) : undefined,
        repliesCount: Number.isFinite(Number(repliesRaw)) ? Number(repliesRaw) : undefined,
      };
    };
    const appendCommentTree = (comment: Record<string, unknown>, postUrl: string) => {
      const row = toComment(comment, postUrl);
      const key = `${row.id}|${row.authorProfileUrl}|${row.text}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(row);
      }
      const replies = toArray(comment.replies);
      for (const reply of replies) appendCommentTree(reply, postUrl);
    };

    for (const item of items) {
      const postUrl = String(item.post_url ?? item.url ?? item.inputUrl ?? item.postUrl ?? '');
      const postAuthorName = String(
        item.author_username ??
        ((item.author as Record<string, unknown> | undefined)?.name ?? '')
      ).trim();
      const postAuthorProfileRaw = String(
        item.author_profile_url ??
        ((item.author as Record<string, unknown> | undefined)?.url ?? '')
      ).trim();
      const postAuthorUserId = String(item.author_user_id ?? '').trim();
      const postAuthorProfile =
        postAuthorProfileRaw ||
        (/^\d+$/.test(postAuthorUserId) ? `https://www.facebook.com/profile.php?id=${postAuthorUserId}` : '');
      if (postAuthorName) {
        const createdRaw = Number(item.create_time ?? item.timestamp ?? 0);
        const createdMs =
          Number.isFinite(createdRaw) && createdRaw > 0
            ? createdRaw < 1_000_000_000_000 ? createdRaw * 1000 : createdRaw
            : 0;
        const authorRow: ListenerComment = {
          id: `post_author:${String(item.post_id ?? postUrl ?? postAuthorName)}`,
          postUrl,
          authorName: postAuthorName,
          authorProfileUrl: postAuthorProfile,
          text: '[Original post author]',
          postedAt: createdMs > 0 ? new Date(createdMs).toISOString() : '—',
        };
        const authorKey = `${authorRow.id}|${authorRow.authorProfileUrl}|${authorRow.text}`;
        if (!seen.has(authorKey)) {
          seen.add(authorKey);
          out.push(authorRow);
        }
      }
      const commentsCandidate = item.comments;
      const commentBuckets = [
        ...toArray(commentsCandidate),
        ...toArray(item.post_comments),
        ...toArray(item.latestComments),
        ...toArray(item.latest_comments),
        ...toArray(item.previewComments),
        ...toArray(item.topComments),
        ...toArray(item.top_comments),
        ...toArray((item.edge_media_to_parent_comment as Record<string, unknown> | undefined)?.edges
          ? item.edge_media_to_parent_comment
          : undefined),
      ];
      const isFlatCommentRow =
        typeof item.commentUrl === 'string' ||
        typeof item.feedbackId === 'string' ||
        (typeof item.text === 'string' && (typeof item.profileName === 'string' || typeof item.profileUrl === 'string'));
      if (isFlatCommentRow) {
        appendCommentTree(item, postUrl || String(item.facebookUrl ?? item.inputUrl ?? item.commentUrl ?? ''));
      }
      if (commentBuckets.length > 0) {
        for (const c of commentBuckets) {
          if (!c || typeof c !== 'object') continue;
          appendCommentTree(c as Record<string, unknown>, postUrl);
        }
      }
    }
    return out.slice(0, 1000);
  };

  const deepComments = useMemo(() => {
    if (!activeKeyword) return [] as ListenerComment[];
    return extractCommentsFromDeepResults(listenerDeepResults[activeKeyword] || []);
  }, [activeKeyword, listenerDeepResults]);

  const deepCommentsByPost = useMemo(() => {
    const map: Record<string, ListenerComment[]> = {};
    for (const c of deepComments) {
      const urlKey = normalizePostUrl(c.postUrl) || '__unknown__';
      if (!map[urlKey]) map[urlKey] = [];
      map[urlKey].push(c);

      const activityId = extractLinkedInActivityId(c.postUrl);
      if (activityId) {
        const activityKey = `activity:${activityId}`;
        if (!map[activityKey]) map[activityKey] = [];
        map[activityKey].push(c);
      }
      const igCode = extractInstagramShortCode(c.postUrl);
      if (igCode) {
        const igKey = `ig:${igCode}`;
        if (!map[igKey]) map[igKey] = [];
        map[igKey].push(c);
      }
    }
    return map;
  }, [deepComments]);

  const modalComments = useMemo(() => {
    if (!commentsModal.open) return [] as ListenerComment[];
    const urlKey = normalizePostUrl(commentsModal.postUrl);
    const byUrl = deepCommentsByPost[urlKey] || [];
    if (byUrl.length > 0) return byUrl;

    const activityId = extractLinkedInActivityId(commentsModal.postUrl);
    if (activityId) {
      const byActivity = deepCommentsByPost[`activity:${activityId}`] || [];
      if (byActivity.length > 0) return byActivity;
    }
    const igCode = extractInstagramShortCode(commentsModal.postUrl);
    if (igCode) {
      const byIgCode = deepCommentsByPost[`ig:${igCode}`] || [];
      if (byIgCode.length > 0) return byIgCode;
    }

    // If we have only one deep-scraped post loaded, show its comments as fallback.
    if (deepComments.length > 0 && Object.keys(deepCommentsByPost).length <= 3) return deepComments;
    return [];
  }, [commentsModal, deepCommentsByPost]);

  const modalCommentPages = Math.max(1, Math.ceil(modalComments.length / 10));
  const pagedModalComments = useMemo(() => {
    const start = (commentPage - 1) * 10;
    return modalComments.slice(start, start + 10);
  }, [modalComments, commentPage]);

  const sortedListenerRows = useMemo(() => {
    if (!isListenerTab || !activeRun?.results) return activeRun?.results || [];
    const rows = [...activeRun.results];
    rows.sort((a, b) => {
      const left = a as Record<string, unknown>;
      const right = b as Record<string, unknown>;
      let diff = 0;
      if (listenerSortField === 'author') {
        diff = getListenerAuthor(left).localeCompare(getListenerAuthor(right), undefined, { sensitivity: 'base' });
      } else if (listenerSortField === 'comments') {
        const lc = getListenerCommentCount(left) ?? -1;
        const rc = getListenerCommentCount(right) ?? -1;
        diff = lc - rc;
      } else {
        diff = getListenerPostedTimestamp(left) - getListenerPostedTimestamp(right);
      }
      return listenerSortDirection === 'asc' ? diff : -diff;
    });
    return rows;
  }, [isListenerTab, activeRun?.results, listenerSortField, listenerSortDirection]);

  const dedupedKeywordRows = useMemo(() => {
    if (isListenerTab || !activeRun?.results) return [] as Array<Record<string, unknown>>;
    const deduped = new Map<string, Record<string, unknown>>();
    for (const item of activeRun.results) {
      const key =
        String(
          item.postUrl ??
            item.post_url ??
            item.url ??
            item.full_urn ??
            item.activity_id ??
            item.id ??
            ''
        ) || JSON.stringify(item);
      if (!deduped.has(key)) deduped.set(key, item);
    }
    return Array.from(deduped.values());
  }, [isListenerTab, activeRun?.results]);

  const keywordLanguageOptions = useMemo(() => {
    const languages = new Set<string>();
    for (const row of dedupedKeywordRows) {
      const code = getRowLanguageCode(row);
      if (code) languages.add(code);
    }
    return ['all', ...Array.from(languages).sort()];
  }, [dedupedKeywordRows]);

  const sortedKeywordRows = useMemo(() => {
    if (isListenerTab) return [] as Array<Record<string, unknown>>;
    let rows = [...dedupedKeywordRows];
    if (keywordLanguageFilter !== 'all') {
      rows = rows.filter((row) => getRowLanguageCode(row) === keywordLanguageFilter);
    }
    rows.sort((a, b) => {
      const left = a as Record<string, unknown>;
      const right = b as Record<string, unknown>;
      let diff = 0;
      if (keywordSortField === 'author') {
        diff = getListenerAuthor(left).localeCompare(getListenerAuthor(right), undefined, { sensitivity: 'base' });
      } else if (keywordSortField === 'comments') {
        const lc = getListenerCommentCount(left) ?? -1;
        const rc = getListenerCommentCount(right) ?? -1;
        diff = lc - rc;
      } else {
        diff = getListenerPostedTimestamp(left) - getListenerPostedTimestamp(right);
      }
      return keywordSortDirection === 'asc' ? diff : -diff;
    });
    return rows;
  }, [isListenerTab, dedupedKeywordRows, keywordLanguageFilter, keywordSortField, keywordSortDirection]);

  const keywordResultsPages = Math.max(1, Math.ceil(sortedKeywordRows.length / KEYWORD_RESULTS_PAGE_SIZE));
  const pagedKeywordRows = useMemo(() => {
    const safePage = Math.max(1, Math.min(keywordResultsPage, keywordResultsPages));
    const start = (safePage - 1) * KEYWORD_RESULTS_PAGE_SIZE;
    return sortedKeywordRows.slice(start, start + KEYWORD_RESULTS_PAGE_SIZE);
  }, [sortedKeywordRows, keywordResultsPage, keywordResultsPages]);

  useEffect(() => {
    setKeywordResultsPage(1);
  }, [activeKeyword, keywordLanguageFilter, keywordSortField, keywordSortDirection]);

  const toggleListenerSelection = (url: string, checked: boolean) => {
    setSelectedListenerUrls((prev) => {
      const next = new Set(prev);
      if (checked) next.add(url);
      else next.delete(url);
      return Array.from(next);
    });
  };

  const runSelectedListenerDeepScrape = async () => {
    if (!userEmail || !activeRun || selectedListenerUrls.length === 0) return;
    const lower = selectedListenerUrls.map((u) => u.toLowerCase());
    const allLinkedin = lower.every((u) => u.includes('linkedin.com'));
    const allInstagram = lower.every((u) => u.includes('instagram.com'));
    const allFacebook = lower.every((u) => u.includes('facebook.com'));
    const platform =
      allLinkedin
        ? 'linkedin'
        : allInstagram
          ? 'instagram'
          : allFacebook
            ? 'facebook'
            : activeRun.source === 'linkedin_listeners'
              ? 'linkedin'
              : activeRun.source === 'facebook_listeners'
                ? 'facebook'
                : 'instagram';
    const term = activeRun.term;
    const wantsAutoComment = listenerAutoCommentEnabled && Boolean(listenerAutoCommentText.trim());
    if (wantsAutoComment && (platform === 'linkedin' || platform === 'instagram')) {
      const connected = platform === 'linkedin' ? commentConnectors.linkedin : commentConnectors.instagram;
      if (!connected) {
        alert(`${platform === 'linkedin' ? 'LinkedIn' : 'Instagram'} connector is not connected.`);
        return;
      }
      const requestedPosts = Math.max(1, Math.min(listenerAutoCommentMaxPosts, selectedListenerUrls.length));
      const requiredCredits = requestedPosts * 2;
      if (requiredCredits > credits) {
        alert(`Insufficient credits. Need ${requiredCredits}, have ${credits}. Shortfall: ${requiredCredits - credits}.`);
        return;
      }
      if (requestedPosts > 50) {
        alert('Max 50 comments/day. Reduce selected posts.');
        return;
      }
    }

    enqueueScrapeJob(
      { source: 'listener_deep', companyName: term, label: `Listener Deep Scrape - ${term}` },
      async (update) => {
        try {
          setListenerDeepStatus('running');
          setListenerDeepError('');
          setListenerAutoCommentStatus('');
          update(10, 'Starting deep scrape…');
          const startRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners/deep`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userEmail,
              platform,
              scraper: listenerScraper,
              urls: selectedListenerUrls,
              limitPerSource: 100,
              deepScrape: true,
              rawData: false,
              resultsType: activeRun.source === 'instagram_reels' ? 'reels' : 'posts',
              resultsLimit: 100,
              includeNestedComments: true,
              viewOption: 'RECENT_ACTIVITY',
              comments_limit: 100,
              commentsLimit: 100,
              maxComments: 100,
              autoCommentEnabled: listenerAutoCommentEnabled && Boolean(listenerAutoCommentText.trim()),
              autoCommentText: listenerAutoCommentText.trim(),
              autoCommentMaxPosts: listenerAutoCommentMaxPosts,
            }),
          });
          const startData = await startRes.json();
          if (!startData.success || !startData.run_id) throw new Error(startData.error || 'Deep scrape failed to start');
          const launch = (startData.commentLaunch || {}) as Record<string, unknown>;
          if (Boolean(launch.attempted)) {
            const ok = Boolean(launch.success);
            const posted = Number(launch.postedCount ?? 0) || 0;
            const err = String(launch.error ?? '').trim();
            setListenerAutoCommentStatus(
              ok ? `Auto-comment launched (${posted} posts).` : `Auto-comment failed: ${err || 'unknown error'}`
            );
          } else {
            setListenerAutoCommentStatus('');
          }

          update(50, 'Polling deep scrape results…');
          const data = await pollAdRun(startData.run_id, {
            source: 'social_listeners',
            keyword: term,
          });
          const results = Array.isArray(data?.results) ? data.results : [];
          setListenerDeepResults((prev) => ({ ...prev, [activeRun.key]: results as Array<Record<string, unknown>> }));
          await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/catalog/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userEmail,
              itemType: 'listener_deep',
              term,
              source: activeRun.source,
              mode: activeRun.mode,
              runId: startData.run_id,
              datasetId: startData.dataset_id,
                selectedUrls: selectedListenerUrls,
                results,
                meta: { scraper: listenerScraper, selectedCount: selectedListenerUrls.length },
              }),
            }).catch(() => undefined);
          setCommentPage(1);
          setListenerDeepStatus('succeeded');
          update(100, 'Complete');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Deep scrape failed';
          setListenerDeepError(message);
          setListenerDeepStatus('failed');
          throw new Error(message);
        }
      }
    );
  };

  useEffect(() => {
    setSelectedListenerUrls([]);
    setListenerDeepStatus('idle');
    setListenerDeepError('');
    setListenerAutoCommentStatus('');
    setCommentPage(1);
    setCommentActionStatus({});
  }, [activeKeyword]);

  const runCommentAuthorResearch = async (comment: ListenerComment) => {
    if (!userEmail || !comment.authorProfileUrl) return;
    const key = comment.id;
    setCommentActionStatus((prev) => ({ ...prev, [key]: 'running' }));
    try {
      const searchResultId = `listener-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const platform = (comment.platform || inferPlatformFromPostUrl(comment.postUrl || comment.authorProfileUrl)).toLowerCase();

      if (platform === 'linkedin') {
        const scrapeRes = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/scrape`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            searchResultId,
            platform: 'linkedin',
            url: comment.authorProfileUrl,
            postedLimit: '6months',
          }),
        });
        const scrapeData = await scrapeRes.json();
        if (!scrapeData?.success) throw new Error(scrapeData?.error || 'Profile scrape failed');

        const profileRunId = String(scrapeData?.runs?.profile?.run_id ?? '').trim();
        const postsRunId = String(scrapeData?.runs?.posts?.run_id ?? '').trim();
        const pollOne = async (runId: string, kind: 'profile' | 'posts') => {
          if (!runId) return;
          for (let i = 0; i < 40; i += 1) {
            await new Promise((r) => setTimeout(r, 3000));
            const statusRes = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/scrape/status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userEmail,
                searchResultId,
                platform: 'linkedin',
                run_id: runId,
                kind,
              }),
            });
            const statusData = await statusRes.json();
            if (!statusData?.success) throw new Error(statusData?.error || 'Status check failed');
            if (statusData.status === 'SUCCEEDED') return statusData;
            if (statusData.status === 'FAILED') throw new Error('LinkedIn scrape failed');
          }
          return null;
        };

        const profileStatus = await pollOne(profileRunId, 'profile');
        await pollOne(postsRunId, 'posts');

        const profileItems = Array.isArray((profileStatus as any)?.data) ? (profileStatus as any).data : [];
        const first = (profileItems[0] && typeof profileItems[0] === 'object' ? profileItems[0] : {}) as Record<string, unknown>;
        const fullName =
          String(
            first.fullName ??
            first.name ??
            `${first.firstName ?? ''} ${first.lastName ?? ''}`
          ).trim();
        const headline = String(first.headline ?? first.title ?? '').trim();
        const companyName = String(
          first.companyName ??
          first.company ??
          ((first.currentCompany as Record<string, unknown> | undefined)?.name ?? '')
        ).trim();
        const enrichedLead = {
          searchResultId,
          contactSearchId: 0,
          name: fullName || (comment.authorName === '—' ? 'LinkedIn User' : comment.authorName),
          linkedInUrl: comment.authorProfileUrl,
          liUrl: comment.authorProfileUrl,
          companyName: companyName || 'LinkedIn Prospect',
          title: headline || '',
        } as any;
        if (!enrichedLead.name || enrichedLead.name === '—') {
          enrichedLead.name = comment.authorName === '—' ? 'LinkedIn User' : comment.authorName;
        }
        const selectedCampaignId =
          typeof window !== 'undefined' ? String(localStorage.getItem(PEPAI_CAMPAIGN_STORAGE_KEY) || '').trim() : '';
        const pepaiEnabledForRerun = Boolean(selectedCampaignId);
        await researchContact(enrichedLead, userEmail, {
          preserveSearchResultId: true,
          pepaiResearch: pepaiEnabledForRerun,
          extraTags: pepaiEnabledForRerun ? ['PEPAI-research'] : [],
          instantlyCampaignId: selectedCampaignId || undefined,
        });
      } else {
        const scraper =
          platform === 'facebook'
            ? 'facebook_page_feed'
            : 'instagram_profile';
        const startRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners/deep`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            platform,
            scraper,
            urls: [comment.authorProfileUrl],
            resultsLimit: platform === 'facebook' ? 20 : 50,
            comments_limit: 25,
            commentsLimit: 25,
            maxComments: 25,
            includeNestedComments: true,
            target: 'all',
            include_raw_data: true,
            reels_count: 12,
          }),
        });
        const startData = await startRes.json();
        if (!startData?.success || !startData?.run_id) throw new Error(startData?.error || 'Profile scrape failed');
        const data = await pollAdRun(String(startData.run_id), {
          source: 'social_listeners',
          keyword: activeRun?.term || comment.authorName || 'listener',
        });
        const results = Array.isArray(data?.results) ? data.results : [];
        await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/catalog/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            itemType: 'listener_author_research',
            term: activeRun?.term || comment.authorName,
            source: activeRun?.source || `${platform}_listeners`,
            mode: activeRun?.mode || 'keyword',
            runId: startData.run_id,
            datasetId: startData.dataset_id,
            selectedUrls: [comment.authorProfileUrl],
            results,
            meta: {
              platform,
              scraper,
              authorName: comment.authorName,
              authorProfileUrl: comment.authorProfileUrl,
              sourcePostUrl: comment.postUrl,
            },
          }),
        }).catch(() => undefined);
      }
      setCommentActionStatus((prev) => ({ ...prev, [key]: 'succeeded' }));
    } catch {
      setCommentActionStatus((prev) => ({ ...prev, [key]: 'failed' }));
    }
  };

  const postCommentToListenerPost = async (row: Record<string, unknown>) => {
    if (!userEmail) return;
    const url = getListenerUrl(row);
    if (!url) return;
    const platform = inferPlatformFromPostUrl(url);
    if (platform !== 'linkedin' && platform !== 'instagram') {
      alert('Comments are supported for LinkedIn and Instagram posts only.');
      return;
    }
    const connected = platform === 'linkedin' ? commentConnectors.linkedin : commentConnectors.instagram;
    if (!connected) {
      alert(`${platform === 'linkedin' ? 'LinkedIn' : 'Instagram'} connector is not connected.`);
      return;
    }
    if (credits < 2) {
      alert(`Insufficient credits. Need 2, have ${credits}. Shortfall: ${Math.max(0, 2 - credits)}.`);
      return;
    }
    const commentText = window.prompt('Enter comment text');
    if (!commentText || !commentText.trim()) return;

    setPostCommentStatus((prev) => ({ ...prev, [url]: 'running' }));
    try {
      const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          platform,
          url,
          commentText: commentText.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Comment failed');
      setPostCommentStatus((prev) => ({ ...prev, [url]: 'succeeded' }));
      alert('Comment launched.');
    } catch (error: any) {
      setPostCommentStatus((prev) => ({ ...prev, [url]: 'failed' }));
      alert(error?.message || 'Comment failed');
    }
  };

  const loadListenerCatalog = async () => {
    if (!userEmail) {
      setCatalogError('Missing userEmail');
      return;
    }
    setCatalogLoading(true);
    setCatalogError('');
    try {
      const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/catalog/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, limit: 50 }),
      });
      const raw = await res.text();
      let data: any = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { success: false, error: raw || `HTTP ${res.status}` };
      }
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      if (data?.success && Array.isArray(data.items)) {
        setCatalogItems(data.items as ListenerCatalogItem[]);
      } else {
        setCatalogItems([]);
        setCatalogError(data?.error || 'Catalog load failed');
      }
    } catch (error) {
      setCatalogItems([]);
      setCatalogError(error instanceof Error ? error.message : 'Catalog load failed');
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadListenerSchedules = async () => {
    if (!userEmail) {
      setListenerSchedulesError('Missing userEmail');
      return;
    }
    setListenerSchedulesLoading(true);
    setListenerSchedulesError('');
    try {
      const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/schedule/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to load listener schedules');
      setListenerSchedules(Array.isArray(data.schedules) ? data.schedules : []);
    } catch (error) {
      setListenerSchedules([]);
      setListenerSchedulesError(error instanceof Error ? error.message : 'Failed to load listener schedules');
    } finally {
      setListenerSchedulesLoading(false);
    }
  };

  const restoreCatalogSnapshot = async (item: ListenerCatalogItem) => {
    const ds = String(item.storedDatasetId ?? '').trim();
    if (!ds) return;
    const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/get_results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataset_id: ds }),
    });
    const data = await res.json();
    const results = Array.isArray(data?.results) ? data.results : [];
    if (!Array.isArray(results)) return;
    const term = String(item.term ?? 'catalog');
    const source = (String(item.source || 'facebook') as KeywordSource);
    const mode = (item.mode === 'hashtag' ? 'hashtag' : 'keyword') as ListenerMode;
    const key = buildKeywordRunKey(term, source, mode);
    const nextRun: KeywordRun = { key, term, source, mode, status: 'succeeded', results };
    setKeywordRuns((prev) => {
      const filtered = prev.filter((k) => k.key !== key);
      return [nextRun, ...filtered];
    });
    setActiveKeyword(key);
    if (item.itemType === 'listener_deep') {
      setListenerDeepResults((prev) => ({ ...prev, [key]: results as Array<Record<string, unknown>> }));
    }
  };

  const openCommentsModal = (row: Record<string, unknown>) => {
    const postUrl = getListenerUrl(row);
    setCommentPage(1);
    setCommentsModal({
      open: true,
      postUrl,
      postLabel: getListenerText(row),
    });
  };

  const addListenerToHotList = async (row: Record<string, unknown>) => {
    if (!userEmail) return;
    const platform = getListenerPlatform(row);
    const url = getListenerUrl(row);
    const author = getListenerAuthor(row);
    const title = getListenerText(row);
    const authorProfile = getListenerAuthorProfileUrl(row);
    const topic = activeRun?.term || '';
    const id = `listener:${platform}:${authorProfile || author}`.replace(/\s+/g, '_');
    await addHotListEntityDb(userEmail, {
      id,
      title: author !== '—' ? author : `${platform} listener account`,
      subtitle: topic ? `Topic: ${topic}` : undefined,
      source: 'listener_search',
      platform,
      url: authorProfile || url || undefined,
      payload: {
        topic,
        source: activeRun?.source,
        mode: activeRun?.mode,
        postUrl: url || undefined,
        postExcerpt: title !== '—' ? title : undefined,
        comments: getListenerCommentCount(row),
        postedAt: getListenerPostedAt(row),
      },
    });
    alert('Added to Hot List');
  };

  const inferPlatformFromPostUrl = (url: string): string => {
    const raw = String(url || '').toLowerCase();
    if (raw.includes('facebook.com')) return 'facebook';
    if (raw.includes('instagram.com')) return 'instagram';
    if (raw.includes('tiktok.com')) return 'tiktok';
    if (raw.includes('x.com') || raw.includes('twitter.com')) return 'x';
    if (raw.includes('linkedin.com')) return 'linkedin';
    return 'social';
  };

  const renderListenerPlatformIcon = (platform: string | undefined) => {
    const normalized = String(platform || '').toLowerCase();
    if (normalized === 'facebook') return <Facebook className="w-4 h-4 text-blue-400" />;
    if (normalized === 'instagram') return <Instagram className="w-4 h-4 text-pink-400" />;
    if (normalized === 'linkedin') return <Linkedin className="w-4 h-4 text-sky-400" />;
    if (normalized === 'x') return <Twitter className="w-4 h-4 text-gray-300" />;
    return <span className="text-xs text-gray-500">—</span>;
  };

  const getListenerPepaiBadges = (comment: ListenerComment) => {
    const badges: Array<{ key: string; label: string; className: string }> = [];
    if (String(comment.platform || '').toLowerCase() === 'facebook') {
      badges.push({
        key: `${comment.id}-fb`,
        label: 'FB PEPai',
        className: 'border-blue-700 bg-blue-950/50 text-blue-300',
      });
    }
    if (comment.email) {
      badges.push({
        key: `${comment.id}-email`,
        label: 'Email PEPai',
        className: 'border-emerald-700 bg-emerald-950/50 text-emerald-300',
      });
    }
    return badges;
  };

  const addCommentAuthorToHotList = async (comment: ListenerComment) => {
    if (!userEmail) return;
    const platform = inferPlatformFromPostUrl(comment.postUrl);
    const topic = activeRun?.term || '';
    const account = comment.authorUsername || comment.authorName || 'unknown';
    const id = `listener_author:${platform}:${comment.authorProfileUrl || account}`.replace(/\s+/g, '_');
    await addHotListEntityDb(userEmail, {
      id,
      title: comment.authorName || account,
      subtitle: topic ? `Topic: ${topic}` : undefined,
      source: 'listener_search',
      platform,
      url: comment.authorProfileUrl || undefined,
      payload: {
        account,
        topic,
        source: activeRun?.source,
        mode: activeRun?.mode,
        conversationPost: commentsModal.postLabel,
        conversationPostUrl: comment.postUrl || commentsModal.postUrl || undefined,
        latestComment: comment.text,
        commentedAt: comment.postedAt,
      },
    });
    alert('Author added to Hot List');
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{pageMeta.title}</h2>
            <p className="text-sm text-gray-400 mt-1">{pageMeta.subtitle}</p>
          </div>
          <div className="text-sm text-gray-400">1 credit per company, keyword, or listener search</div>
        </div>
      </div>

      <div className="bg-card-dark border-b border-gray-800 px-6 py-3">
        <div className="flex gap-3 text-sm">
          <button
            onClick={() => switchTab('companies')}
            className={activeTab === 'companies' ? 'text-white font-semibold' : 'text-gray-400 hover:text-white'}
          >
            Company Ads
          </button>
          <button
            onClick={() => switchTab('keywords')}
            className={activeTab === 'keywords' ? 'text-white font-semibold' : 'text-gray-400 hover:text-white'}
          >
            Trending Keyword Analysis
          </button>
          <button
            onClick={() => switchTab('listeners')}
            className={activeTab === 'listeners' ? 'text-white font-semibold' : 'text-gray-400 hover:text-white'}
          >
            Social Media Listener
          </button>
          <button
            onClick={() => switchTab('groups')}
            className={activeTab === 'groups' ? 'text-white font-semibold' : 'text-gray-400 hover:text-white'}
          >
            Group Scraping
          </button>
        </div>
      </div>

      {activeTab === 'companies' && (
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-4">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex-1 min-w-[200px] max-w-md relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search company name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-400">Show:</label>
                <select
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                </select>
              </div>
              <button
                onClick={() => {
                  if (!userEmail) return;
                  setLoadingCompanies(true);
                  loadCompanies(userEmail)
                    .then((rows) => {
                      setCompanies(
                        rows.map((c) => ({
                          companyId: c.companyId,
                          companyName: c.companyName,
                          website: c.website || '',
                          facebook: (c as any).facebook || '',
                          linkedin: (c as any).linkedin || '',
                        }))
                      );
                    })
                    .finally(() => setLoadingCompanies(false));
                }}
                disabled={loadingCompanies}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loadingCompanies ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-gray-700">
                    <th className="px-2 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Watch</th>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-blue-300 uppercase bg-card-dark">Website</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-amber-300 uppercase bg-card-dark">GoogleAds</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-indigo-300 uppercase bg-card-dark">MetaAds</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-sky-300 uppercase bg-card-dark">LinkedIn Ads</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-gray-300 uppercase bg-card-dark">Custom 1</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-gray-300 uppercase bg-card-dark">Custom 2</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCompanies.length === 0 ? (
                    <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">No companies found</td>
                  </tr>
                ) : (
                    paginatedCompanies.map((c) => {
                      const loading = scrapeLoading[c.companyId] || {};
                      const adBusy = adLoading[c.companyId] || {};
                      const cached = adCache[c.companyId] || {};
                      return (
                        <tr key={c.companyId} className="border-b border-gray-800 hover:bg-gray-900/50">
                          <td className="px-2 py-3 text-center">
                            <input type="checkbox" />
                          </td>
                          <td className="px-2 py-3">
                            <div className="font-semibold text-white">{c.companyName}</div>
                            {adStatus[c.companyId] && (
                              <div className="text-xs text-gray-500 mt-1">{adStatus[c.companyId]}</div>
                            )}
                          </td>
                          <td className="px-2 py-3 text-center">
                            <button
                              onClick={() => {
                                const cachedSite = siteCache[c.companyId]?.website;
                                if (cachedSite?.data?.length) {
                                  setDataDrawer({ companyId: c.companyId, companyName: c.companyName, type: 'website' });
                                  return;
                                }
                                handleScrapeWebsite(c);
                              }}
                              disabled={loading.website}
                              className="px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)' }}
                            >
                              {loading.website ? '...' : (siteCache[c.companyId]?.website?.data?.length ? 'View' : '5cr')}
                            </button>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <button
                              onClick={() => openAdDrawer(c, 'google')}
                              disabled={adBusy.google}
                              className="px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                            >
                              {adBusy.google ? '...' : (cached.google?.results?.length ? 'View' : '1cr')}
                            </button>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <button
                              onClick={() => openAdDrawer(c, 'facebook')}
                              disabled={adBusy.facebook}
                              className="px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                            >
                              {adBusy.facebook ? '...' : (cached.facebook?.results?.length ? 'View' : '1cr')}
                            </button>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <button
                              onClick={() => openAdDrawer(c, 'linkedin')}
                              disabled={adBusy.linkedin}
                              className="px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)' }}
                            >
                              {adBusy.linkedin ? '...' : (cached.linkedin?.results?.length ? 'View' : '1cr')}
                            </button>
                          </td>
                          <td className="px-2 py-3 text-center text-gray-400">—</td>
                          <td className="px-2 py-3 text-center text-gray-400">—</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="px-1 py-2 flex items-center justify-between text-sm text-gray-400">
            <div>Showing {filteredCompanies.length > 0 ? startIdx + 1 : 0}-{endIdx} of {filteredCompanies.length}</div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-50"
              >
                ← Prev
              </button>
              <span>Page {currentPage} of {totalPages || 1}</span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-50"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'groups' && (
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Platform</label>
                <select
                  value={groupPlatform}
                  onChange={(e) => setGroupPlatform(e.target.value as GroupPlatform)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                >
                  <option value="linkedin">LinkedIn Groups</option>
                  <option value="facebook">Facebook Groups</option>
                  <option value="reddit">Reddit Communities</option>
                  <option value="instagram">Instagram Groups/DM Threads</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Workflow</label>
                <select
                  value={groupAction}
                  onChange={(e) => setGroupAction(e.target.value as GroupAction)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                >
                  <option value="listening">Listening</option>
                  <option value="comment_scraping">Comment Scraping</option>
                  <option value="member_scraping">Member Scraping</option>
                  <option value="posting">Posting</option>
                  <option value="commenting">Commenting</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Group Query / URL</label>
                <input
                  value={groupQuery}
                  onChange={(e) => setGroupQuery(e.target.value)}
                  placeholder="Group URL, name, subreddit, or keyword"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Agent ID (posting/commenting/member)</label>
                <input
                  value={groupAgentId}
                  onChange={(e) => setGroupAgentId(e.target.value)}
                  placeholder={groupAction === 'member_scraping' ? (groupPlatform === 'facebook' ? '3807725832561121' : groupPlatform === 'linkedin' ? '1415790306296130' : 'Agent ID') : 'Agent ID if required'}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Post Text</label>
                <input
                  value={groupPostText}
                  onChange={(e) => setGroupPostText(e.target.value)}
                  placeholder="Post content for posting workflows"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Comment Text</label>
                <input
                  value={groupCommentText}
                  onChange={(e) => setGroupCommentText(e.target.value)}
                  placeholder="Comment content for commenting workflows"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-2">
              <button onClick={() => runGroupWorkflow('listening')} className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm">Start Listening</button>
              <button onClick={() => runGroupWorkflow('comment_scraping')} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">Scrape Comments</button>
              <button onClick={() => runGroupWorkflow('member_scraping')} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">Scrape Members</button>
              <button onClick={() => runGroupWorkflow('posting')} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">Create Post</button>
              <button onClick={() => runGroupWorkflow('commenting')} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">Post Comment</button>
            </div>

            <div className="mt-4 text-xs text-gray-400">
              {groupRunStatus || `Selected: ${groupPlatform} · ${groupAction.replace('_', ' ')}`}
            </div>
          </div>
        </div>
      )}

      {(activeTab === 'keywords' || activeTab === 'listeners') && (
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6">
            <div className="flex items-center gap-3">
              <input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword();
                  }
                }}
                placeholder={activeTab === 'listeners' ? 'Type a keyword or hashtag, then press Enter' : 'Type a keyword or phrase, then press Enter'}
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
              <button
                onClick={handleAddKeyword}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm"
              >
                Add
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Source</label>
                <select
                  value={keywordSource}
                  onChange={(e) => setKeywordSource(e.target.value as KeywordSource)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                >
                  {activeTab === 'listeners' ? (
                    <>
                      <option value="instagram_posts">Instagram Posts</option>
                      <option value="instagram_reels">Instagram Reels</option>
                      <option value="linkedin_listeners">LinkedIn Posts</option>
                      <option value="facebook_listeners">Facebook Posts</option>
                      <option value="tiktok_listeners">TikTok Posts</option>
                      <option value="x_listeners">X Posts</option>
                    </>
                  ) : (
                    <>
                      <option value="linkedin_ads">LinkedIn Ads</option>
                      <option value="google_trending">Google Trending Topics</option>
                      <option value="instagram_trending">Instagram Trending Keywords</option>
                      <option value="facebook_trending">Facebook Trending Keywords</option>
                      <option value="linkedin_viral">LinkedIn Viral Posts by Subject</option>
                      <option value="tiktok_trending">TikTok Trending Hashtags</option>
                      <option value="x_trending">X Trending Terms</option>
                    </>
                  )}
                </select>
              </div>
              {activeTab === 'listeners' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Mode</label>
                  <select
                    value={listenerMode}
                    onChange={(e) => setListenerMode(e.target.value as ListenerMode)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  >
                    <option value="keyword">Keyword</option>
                    <option value="hashtag">Hashtag</option>
                  </select>
                </div>
              )}
              {(keywordSource === 'tiktok_listeners' || keywordSource === 'tiktok_trending') && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">TikTok Input Mode</label>
                  <select
                    value={tiktokMode}
                    onChange={(e) => setTikTokMode(e.target.value as TikTokMode)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  >
                    <option value="terms">Terms</option>
                    <option value="profiles">Profiles</option>
                    <option value="post_urls">Post URLs</option>
                  </select>
                </div>
              )}
              {activeTab === 'listeners' && keywordSource === 'facebook_listeners' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Facebook Location (optional)</label>
                  <input
                    value={facebookListenerLocation}
                    onChange={(e) => setFacebookListenerLocation(e.target.value)}
                    placeholder="e.g., Dallas, Texas"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
              )}
              {(keywordSource === 'tiktok_listeners' || keywordSource === 'tiktok_trending') && tiktokMode === 'terms' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">TikTok Search Queries (optional)</label>
                  <input
                    value={tiktokSearchQueriesInput}
                    onChange={(e) => setTikTokSearchQueriesInput(e.target.value)}
                    placeholder="comma-separated, e.g. booktok,selfpublished"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
              )}
              {(keywordSource === 'tiktok_listeners' || keywordSource === 'tiktok_trending') && tiktokMode === 'profiles' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">TikTok Profiles</label>
                  <input
                    value={tiktokProfilesInput}
                    onChange={(e) => setTikTokProfilesInput(e.target.value)}
                    placeholder="comma-separated, e.g. profitibull,@creator"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
              )}
              {(keywordSource === 'tiktok_listeners' || keywordSource === 'tiktok_trending') && tiktokMode === 'post_urls' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">TikTok Post URLs</label>
                  <input
                    value={tiktokPostUrlsInput}
                    onChange={(e) => setTikTokPostUrlsInput(e.target.value)}
                    placeholder="comma-separated https://www.tiktok.com/@.../video/..."
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
              )}
              {activeTab === 'keywords' && (
                <>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Company (optional)</label>
                    <select
                      value={keywordCompanyId}
                      onChange={(e) => setKeywordCompanyId(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                    >
                      <option value="">Not linked</option>
                      {companies.map((c) => (
                        <option key={c.companyId} value={c.companyId}>
                          {c.companyName}
                        </option>
                      ))}
                    </select>
                  </div>
                  {keywordSource === 'linkedin_ads' && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">LinkedIn Account Owner</label>
                      <input
                        value={linkedinOwner}
                        onChange={(e) => setLinkedinOwner(e.target.value)}
                        placeholder="e.g., nvidia"
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      />
                    </div>
                  )}
                  {keywordSource === 'linkedin_viral' && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Target Posts (paged)</label>
                      <input
                        type="number"
                        min={20}
                        max={500}
                        step={20}
                        value={linkedinViralTargetPosts}
                        onChange={(e) =>
                          setLinkedinViralTargetPosts(Math.max(20, Math.min(500, Number(e.target.value) || 20)))
                        }
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      />
                      <div className="text-[11px] text-gray-500 mt-1">
                        Uses 20 posts/page with startPage 1..N and merges deduped results.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs text-gray-400">Keyword History</div>
                <button
                  onClick={() => setShowKeywordHistory((prev) => !prev)}
                  className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-200 hover:bg-gray-700"
                >
                  {showKeywordHistory ? 'Hide' : 'Show'}
                </button>
              </div>
              {showKeywordHistory ? (
                <div className="flex flex-wrap gap-2">
                  {visibleKeywordRuns.map((k) => (
                    <div
                      key={k.key}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                        activeKeyword === k.key ? 'bg-red-600 border-red-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'
                      }`}
                    >
                      <button onClick={() => setActiveKeyword(k.key)} className="text-left">
                        {k.term}{k.mode ? ` · ${k.mode}` : ''}
                      </button>
                      <button
                        onClick={() => removeKeywordRun(k.key)}
                        className="opacity-80 hover:opacity-100"
                        aria-label="Remove keyword history item"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {visibleKeywordRuns.length === 0 && (
                    <span className="text-xs text-gray-500">No keyword history for this tool yet.</span>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">Keyword history hidden.</div>
              )}
            </div>
          </div>

          {activeRun && (
            <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Keyword</div>
                  <div className="text-lg font-semibold">{activeRun.term}</div>
                </div>
                <div className="flex items-center gap-2">
                  {isListenerTab && (
                    <>
                      <select
                        value={listenerSortField}
                        onChange={(e) => setListenerSortField(e.target.value as ListenerSortField)}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      >
                        <option value="date">Sort: Date</option>
                        <option value="author">Sort: Author</option>
                        <option value="comments">Sort: Comments</option>
                      </select>
                      <select
                        value={listenerSortDirection}
                        onChange={(e) => setListenerSortDirection(e.target.value as SortDirection)}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                      </select>
                      <select
                        value={listenerScraper}
                        onChange={(e) => setListenerScraper(e.target.value as ListenerScraper)}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      >
                        <option value="auto">Deep Scraper (Auto)</option>
                        <option value="linkedin_url">LinkedIn URL Scraper</option>
                        <option value="instagram_direct">Instagram Direct URLs</option>
                        <option value="instagram_profile">Instagram Profile Deep</option>
                        <option value="facebook_post_urls">Facebook Post URLs</option>
                        <option value="facebook_page_feed">Facebook Page Feed</option>
                      </select>
                      {(activeRun.source === 'linkedin_listeners' || activeRun.source === 'instagram_posts' || activeRun.source === 'instagram_reels') && (
                        <>
                          <label className="flex items-center gap-2 text-xs text-gray-300 px-2">
                            <input
                              type="checkbox"
                              checked={listenerAutoCommentEnabled}
                              onChange={(e) => setListenerAutoCommentEnabled(e.target.checked)}
                            />
                            Auto-comment
                          </label>
                          <input
                            value={listenerAutoCommentText}
                            onChange={(e) => setListenerAutoCommentText(e.target.value)}
                            placeholder="Comment text"
                            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[220px]"
                          />
                          <input
                            type="number"
                            min={1}
                            max={25}
                            value={listenerAutoCommentMaxPosts}
                            onChange={(e) => setListenerAutoCommentMaxPosts(Math.max(1, Math.min(25, Number(e.target.value) || 1)))}
                            className="w-20 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                            title="Max posts to comment per run"
                          />
                        </>
                      )}
                      <button
                        onClick={runSelectedListenerDeepScrape}
                        disabled={selectedListenerUrls.length === 0 || listenerDeepStatus === 'running'}
                        className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-white text-sm"
                      >
                        {listenerDeepStatus === 'running' ? 'Scraping…' : `Scrape Selected (${selectedListenerUrls.length})`}
                      </button>
                    </>
                  )}
                  {!isListenerTab && (
                    <>
                      <select
                        value={keywordSortField}
                        onChange={(e) => setKeywordSortField(e.target.value as ListenerSortField)}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      >
                        <option value="comments">Sort: Comments</option>
                        <option value="date">Sort: Date</option>
                        <option value="author">Sort: Author</option>
                      </select>
                      <select
                        value={keywordSortDirection}
                        onChange={(e) => setKeywordSortDirection(e.target.value as SortDirection)}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                      </select>
                      <select
                        value={keywordLanguageFilter}
                        onChange={(e) => setKeywordLanguageFilter(e.target.value)}
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      >
                        <option value="all">Language: All</option>
                        {keywordLanguageOptions.includes('en') && <option value="en">Language: English</option>}
                        {keywordLanguageOptions.filter((code) => code !== 'all' && code !== 'en').map((code) => (
                          <option key={code} value={code}>
                            Language: {code.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <button
                    onClick={() => runKeyword(activeRun)}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm"
                  >
                    {activeTab === 'listeners' ? 'Run (100 results)' : 'Run (10 ads)'}
                  </button>
                  {isListenerTab && (
                    <button
                      onClick={openListenerScheduleModal}
                      className="px-3 py-2 bg-sky-600 hover:bg-sky-700 rounded-lg text-white text-sm"
                    >
                      Schedule
                    </button>
                  )}
                </div>
              </div>

              {activeRun.error && (
                <div className="text-sm text-rose-400">{activeRun.error}</div>
              )}
              {listenerDeepError && (
                <div className="text-sm text-rose-400">Deep scrape error: {listenerDeepError}</div>
              )}
              {listenerAutoCommentStatus && (
                <div className="text-sm text-sky-300">{listenerAutoCommentStatus}</div>
              )}
              {isListenerTab && (
                <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3 text-xs text-gray-300 space-y-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`px-2 py-1 rounded border ${commentConnectors.linkedin ? 'border-green-700 text-green-400' : 'border-red-700 text-red-400'}`}>
                      LinkedIn: {commentConnectors.linkedin ? 'Connected' : 'Not connected'}
                    </span>
                    <span className={`px-2 py-1 rounded border ${commentConnectors.instagram ? 'border-green-700 text-green-400' : 'border-red-700 text-red-400'}`}>
                      Instagram: {commentConnectors.instagram ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                  <div>Comments are posted from your connected real account.</div>
                  <div>2 credits per comment.</div>
                  <div>Max 50 comments/day. Minimum 3 minutes between comments.</div>
                  <div>At max volume, 50 comments will take at least 150 minutes.</div>
                  <div>Replies can be tracked with a separate listener (additional credits).</div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-800">
                      <th className="py-2 pr-4">Actions</th>
                      <th className="py-2 pr-4">Platform</th>
                      <th className="py-2 pr-4">Author</th>
                      <th className="py-2 pr-4">Post</th>
                      <th className="py-2 pr-4">Comments</th>
                      <th className="py-2 pr-4">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isListenerTab ? sortedListenerRows : pagedKeywordRows).map((r, idx) => (
                      <tr key={idx} className="border-b border-gray-900 align-top">
                        {(() => {
                          const postUrl = getListenerUrl(r);
                          const platform = inferPlatformFromPostUrl(postUrl);
                          const canComment = Boolean(postUrl) && (platform === 'linkedin' || platform === 'instagram');
                          const commentStatus = postUrl ? postCommentStatus[postUrl] || 'idle' : 'idle';
                          return (
                            <>
                              <td className="py-2 pr-4 text-gray-400">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {isListenerTab && postUrl && (
                                    <button
                                      type="button"
                                      onClick={() => toggleListenerSelection(postUrl, !selectedListenerUrls.includes(postUrl))}
                                      className={`px-2 py-1 rounded text-[11px] border ${
                                        selectedListenerUrls.includes(postUrl)
                                          ? 'bg-emerald-700 border-emerald-500 text-white'
                                          : 'bg-gray-800 border-gray-700 text-gray-200'
                                      }`}
                                      title="Select for scrape"
                                    >
                                      {selectedListenerUrls.includes(postUrl) ? 'Selected' : 'Select'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => addListenerToHotList(r)}
                                    className="px-2 py-1 rounded bg-orange-700 hover:bg-orange-600 text-[11px] text-white"
                                  >
                                    Hot List
                                  </button>
                                  {canComment && (
                                    <button
                                      onClick={() => postCommentToListenerPost(r)}
                                      disabled={commentStatus === 'running'}
                                      className="px-2 py-1 rounded bg-sky-700 hover:bg-sky-600 disabled:opacity-50 text-[11px] text-white"
                                      title="Post comment on this post"
                                    >
                                      {commentStatus === 'running'
                                        ? 'Commenting...'
                                        : commentStatus === 'succeeded'
                                          ? 'Commented'
                                          : commentStatus === 'failed'
                                            ? 'Retry Comment'
                                            : 'Comment'}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 pr-4 text-gray-300">{getListenerPlatform(r)}</td>
                              <td className="py-2 pr-4 text-gray-300">
                                {getListenerAuthorProfileUrl(r) ? (
                                  <a href={getListenerAuthorProfileUrl(r)} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                                    {getListenerAuthor(r)}
                                  </a>
                                ) : (
                                  getListenerAuthor(r)
                                )}
                              </td>
                              <td className="py-2 pr-4 text-gray-300">
                                {postUrl ? (
                                  <a href={postUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline break-all">
                                    {postUrl}
                                  </a>
                                ) : (
                                  '—'
                                )}
                                <div className="text-gray-500 mt-1">{getListenerText(r)}</div>
                              </td>
                              <td className="py-2 pr-4 text-gray-300">
                                {getListenerCommentCount(r) !== null && getListenerCommentCount(r)! > 0 ? (
                                  <button
                                    onClick={() => openCommentsModal(r)}
                                    className="text-sky-400 hover:text-sky-300 hover:underline"
                                    title="View comments"
                                  >
                                    {getListenerCommentCount(r)}
                                  </button>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="py-2 pr-4 text-gray-400">{getListenerPostedAt(r)}</td>
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                    {(isListenerTab ? sortedListenerRows.length : pagedKeywordRows.length) === 0 && (
                      <tr>
                        <td className="py-4 text-gray-500" colSpan={6}>
                          No results yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!isListenerTab && (
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <div>
                    Showing {Math.min((keywordResultsPage - 1) * KEYWORD_RESULTS_PAGE_SIZE + 1, sortedKeywordRows.length || 0)}-
                    {Math.min(keywordResultsPage * KEYWORD_RESULTS_PAGE_SIZE, sortedKeywordRows.length)} of {sortedKeywordRows.length} deduped posts
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setKeywordResultsPage((p) => Math.max(1, p - 1))}
                      disabled={keywordResultsPage <= 1}
                      className="px-2 py-1 bg-gray-800 border border-gray-700 rounded disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <span>Page {Math.min(keywordResultsPage, keywordResultsPages)} / {keywordResultsPages}</span>
                    <button
                      onClick={() => setKeywordResultsPage((p) => Math.min(keywordResultsPages, p + 1))}
                      disabled={keywordResultsPage >= keywordResultsPages}
                      className="px-2 py-1 bg-gray-800 border border-gray-700 rounded disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {isListenerTab && activeKeyword && Array.isArray(listenerDeepResults[activeKeyword]) && (
                <div className="mt-6">
                  <h4 className="text-sm text-gray-300 mb-2">
                    Deep Scrape Loaded: {deepComments.length} comments
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Click a post's comment count in the table above to open comments modal.
                  </p>
                  <h4 className="text-sm text-gray-300 mt-4 mb-2">Deep Scrape Raw ({listenerDeepResults[activeKeyword].length} items)</h4>
                  <pre className="bg-gray-900 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-auto max-h-80">
                    {JSON.stringify(listenerDeepResults[activeKeyword], null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {isListenerTab && (
            <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Scheduled Listener Keywords</h3>
                <button
                  onClick={() => loadListenerSchedules()}
                  className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-200 hover:bg-gray-700"
                >
                  Refresh
                </button>
              </div>
              {listenerSchedulesLoading ? (
                <p className="text-xs text-gray-500">Loading…</p>
              ) : listenerSchedulesError ? (
                <p className="text-xs text-rose-400">{listenerSchedulesError}</p>
              ) : listenerSchedules.length === 0 ? (
                <p className="text-xs text-gray-500">No scheduled listener keywords yet.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-auto">
                  {listenerSchedules.map((item) => {
                    const config = (item.listenerConfig || {}) as Record<string, unknown>;
                    const term = String(config.term ?? 'listener');
                    const source = String(config.source ?? config.platform ?? 'unknown');
                    return (
                      <div key={item.scheduleId} className="bg-gray-900 border border-gray-800 rounded p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="text-xs">
                            <div className="text-gray-100">{item.name || term}</div>
                            <div className="text-gray-400">{term} · {source}</div>
                            <div className="text-gray-500">
                              {Array.isArray(item.scheduleDays) ? item.scheduleDays.join(', ') : 'No days'} @ {String(item.scheduleHour ?? '—').padStart(2, '0')}:00
                            </div>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            <div>Next: {item.nextRunAt || '—'}</div>
                            <div>Last: {item.lastRunAt || '—'}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isListenerTab && (
            <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Listener Catalog (Persistent)</h3>
                <button
                  onClick={() => loadListenerCatalog()}
                  className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-200 hover:bg-gray-700"
                >
                  Refresh
                </button>
              </div>
              {catalogLoading ? (
                <p className="text-xs text-gray-500">Loading…</p>
              ) : catalogError ? (
                <p className="text-xs text-rose-400">{catalogError}</p>
              ) : catalogItems.length === 0 ? (
                <p className="text-xs text-gray-500">No saved listener snapshots yet.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-auto">
                  {catalogItems.map((item) => (
                    <div key={item.createdAtKey} className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded p-2">
                      <div className="text-xs">
                        <div className="text-gray-200">
                          {item.term || 'snapshot'} · {item.itemType} · {item.source || 'n/a'}
                        </div>
                        <div className="text-gray-500">{item.createdAt}</div>
                      </div>
                      <button
                        onClick={() => restoreCatalogSnapshot(item)}
                        disabled={!item.storedDatasetId}
                        className="px-2 py-1 text-xs rounded bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                      >
                        Load
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {listenerScheduleModal.open && activeRun && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setListenerScheduleModal((prev) => ({ ...prev, open: false, error: '' }))} />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="w-full max-w-lg rounded-xl border border-border-dark bg-card-dark p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">Schedule Social Listener</h3>
                  <p className="text-xs text-gray-400 mt-1">{activeRun.term} · {activeRun.source}</p>
                </div>
                <button onClick={() => setListenerScheduleModal((prev) => ({ ...prev, open: false, error: '' }))} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Schedule Name</label>
                  <input
                    value={listenerScheduleModal.name}
                    onChange={(e) => setListenerScheduleModal((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Days</label>
                  <div className="flex flex-wrap gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleListenerScheduleDay(day)}
                        className={`px-3 py-1 rounded-full text-xs border ${listenerScheduleModal.days.includes(day) ? 'bg-sky-600 border-sky-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Hour (24h)</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={listenerScheduleModal.hour}
                    onChange={(e) => setListenerScheduleModal((prev) => ({ ...prev, hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
                {(activeRun.source === 'linkedin_listeners' || activeRun.source === 'instagram_posts' || activeRun.source === 'instagram_reels') && (
                  <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3 space-y-3">
                    <label className="flex items-center gap-2 text-sm text-gray-200">
                      <input
                        type="checkbox"
                        checked={listenerScheduleModal.autoCommentEnabled}
                        onChange={(e) =>
                          setListenerScheduleModal((prev) => ({ ...prev, autoCommentEnabled: e.target.checked }))
                        }
                      />
                      Auto-comment on scraped posts via PhantomBuster
                    </label>
                    {listenerScheduleModal.autoCommentEnabled && (
                      <>
                        <textarea
                          value={listenerScheduleModal.autoCommentText}
                          onChange={(e) =>
                            setListenerScheduleModal((prev) => ({ ...prev, autoCommentText: e.target.value }))
                          }
                          placeholder="Enter the comment text to post."
                          className="w-full min-h-[90px] px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                        />
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Max posts per run</label>
                          <input
                            type="number"
                            min={1}
                            max={25}
                            value={listenerScheduleModal.autoCommentMaxPosts}
                            onChange={(e) =>
                              setListenerScheduleModal((prev) => ({
                                ...prev,
                                autoCommentMaxPosts: Math.max(1, Math.min(25, Number(e.target.value) || 1)),
                              }))
                            }
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
                {listenerScheduleModal.error && <div className="text-sm text-rose-400">{listenerScheduleModal.error}</div>}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setListenerScheduleModal((prev) => ({ ...prev, open: false, error: '' }))}
                    className="px-3 py-2 bg-gray-800 rounded-lg text-sm text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createListenerSchedule}
                    disabled={listenerScheduleModal.saving}
                    className="px-3 py-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 rounded-lg text-sm text-white"
                  >
                    {listenerScheduleModal.saving ? 'Saving…' : 'Save Schedule'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {commentsModal.open && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setCommentsModal({ open: false, postUrl: '', postLabel: '' })} />
          <div className="fixed top-0 right-0 w-[760px] max-w-[96vw] h-full bg-card-dark border-l border-gray-700 z-50 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">Post Comments</h3>
                  <p className="text-xs text-gray-400 mt-1">{commentsModal.postLabel}</p>
                </div>
                <button
                  onClick={() => setCommentsModal({ open: false, postUrl: '', postLabel: '' })}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="overflow-x-auto border border-gray-800 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-800">
                      <th className="py-2 px-3">Profile Name</th>
                      <th className="py-2 px-3">Post Keyword</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3">Channel</th>
                      <th className="py-2 px-3">Date</th>
                      <th className="py-2 px-3">PEPai</th>
                      <th className="py-2 px-3">Location</th>
                      <th className="py-2 px-3">Comment</th>
                      <th className="py-2 px-3">Profile</th>
                      <th className="py-2 px-3">Details</th>
                      <th className="py-2 px-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedModalComments.map((c) => (
                      <tr key={c.id} className="border-b border-gray-900 align-top">
                        <td className="py-2 px-3 text-gray-300">
                          <div>{c.authorName}</div>
                          {c.authorUsername ? <div className="text-[11px] text-gray-500">@{c.authorUsername}</div> : null}
                        </td>
                        <td className="py-2 px-3 text-gray-300">{activeRun?.term || '—'}</td>
                        <td className="py-2 px-3 text-gray-300">
                          {c.email ? (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-emerald-400" />
                              <span className="break-all">{c.email}</span>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-300">
                          <div className="flex items-center gap-2">
                            {renderListenerPlatformIcon(c.platform)}
                            <span className="capitalize">{c.platform || '—'}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-400">{c.postedAt}</td>
                        <td className="py-2 px-3 text-gray-300">
                          <div className="flex flex-wrap gap-2">
                            {getListenerPepaiBadges(c).length > 0 ? (
                              getListenerPepaiBadges(c).map((badge) => (
                                <span
                                  key={badge.key}
                                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-400">{c.location || '—'}</td>
                        <td className="py-2 px-3 text-gray-300">{c.text}</td>
                        <td className="py-2 px-3 text-blue-400">
                          {c.authorProfileUrl ? (
                            <div className="space-y-1">
                              <a href={c.authorProfileUrl} target="_blank" rel="noreferrer" className="hover:underline">
                                profile
                              </a>
                              {c.authorProfileId ? <div className="text-[11px] text-gray-500">id: {c.authorProfileId}</div> : null}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-400">
                          <div className="text-xs space-y-1">
                            <div>Platform: {c.platform || '—'}</div>
                            <div>Likes: {c.likesCount ?? '—'}</div>
                            <div>Replies: {c.repliesCount ?? '—'}</div>
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => addCommentAuthorToHotList(c)}
                              className="px-2 py-1 rounded text-xs bg-orange-700 hover:bg-orange-600 text-white"
                            >
                              Hot List Author
                            </button>
                            {c.authorProfileUrl ? (
                              <button
                                onClick={() => runCommentAuthorResearch(c)}
                                disabled={commentActionStatus[c.id] === 'running'}
                                className="px-2 py-1 rounded text-xs bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white"
                              >
                                {commentActionStatus[c.id] === 'running'
                                  ? 'Running…'
                                  : commentActionStatus[c.id] === 'succeeded'
                                    ? 'Done'
                                    : commentActionStatus[c.id] === 'failed'
                                      ? 'Retry'
                                      : 'Start Data Search'}
                              </button>
                            ) : (
                              <span className="text-gray-500 self-center">No profile</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pagedModalComments.length === 0 && (
                      <tr>
                        <td className="py-3 px-3 text-gray-500" colSpan={11}>
                          No comments found for this post.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-gray-400">
                <div>Page {commentPage} of {modalCommentPages}</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCommentPage((p) => Math.max(1, p - 1))}
                    disabled={commentPage === 1}
                    className="px-2 py-1 bg-gray-800 rounded disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setCommentPage((p) => Math.min(modalCommentPages, p + 1))}
                    disabled={commentPage >= modalCommentPages}
                    className="px-2 py-1 bg-gray-800 rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {dataDrawer && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setDataDrawer(null)} />
          <div className="fixed top-0 right-0 w-[600px] max-w-[95vw] h-full bg-card-dark border-l border-gray-700 z-50 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{dataDrawer.companyName}</h3>
                  <p className="text-sm text-gray-400">{dataDrawer.type} Data</p>
                </div>
                <button onClick={() => setDataDrawer(null)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {(() => {
                const entry = siteCache[dataDrawer.companyId]?.[dataDrawer.type];
                const payload = entry?.data;
                if (dataDrawer.type === 'website' && payload) {
                  const summary = summarizeWebsiteData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-slate-900 to-gray-900 border border-gray-700 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            {summary.logoUrl ? (
                              <img
                                src={summary.logoUrl}
                                alt={`${dataDrawer.companyName} logo`}
                                className="w-10 h-10 rounded-md object-contain bg-white/90 p-1 border border-gray-700"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : null}
                            <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400">Website Overview</p>
                            <h4 className="text-lg font-semibold text-white">{summary.title || dataDrawer.companyName}</h4>
                            <p className="text-sm text-gray-400 mt-1">{summary.description || 'No meta description found.'}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white">Pages {summary.pagesCount}</span>
                            {summary.canonicalUrl && (
                              <a className="text-xs text-indigo-300 hover:text-indigo-200" href={summary.canonicalUrl} target="_blank" rel="noreferrer">
                                {summary.canonicalUrl.replace(/^https?:\/\//, '')}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Contact</p>
                          <p className="text-sm text-gray-300">{summary.address || 'Address not found'}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(summary.phones || []).length > 0 ? (
                              summary.phones!.slice(0, 3).map((phone) => (
                                <span key={phone} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">{phone}</span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No phone numbers found</span>
                            )}
                          </div>
                        </div>
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Keywords</p>
                          <div className="flex flex-wrap gap-2">
                            {(summary.keywords || []).length > 0 ? (
                              summary.keywords!.slice(0, 8).map((kw) => (
                                <span key={kw} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">{kw}</span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No keywords found</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Social Links</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {Object.entries(summary.socials || {}).map(([key, links]) => (
                            <div key={key} className="bg-gray-800 rounded-lg p-2">
                              <p className="text-gray-400">{key.toUpperCase()}</p>
                              <p className="text-gray-200">{links.length ? links.length : 0}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-2">Content Snapshot</p>
                        <p className="text-sm text-gray-300 leading-relaxed">{summary.sample || 'No page text captured.'}</p>
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'ecommerce' && payload) {
                  const summary = summarizeEcommerceData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-emerald-900/40 to-gray-900 border border-emerald-700/40 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-emerald-300/80">Products Overview</p>
                            <h4 className="text-lg font-semibold text-white">{dataDrawer.companyName}</h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.total > 0 ? `${summary.total} items captured` : 'No products found yet'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white">
                              Products {summary.total}
                            </span>
                            {summary.withPrice > 0 && (
                              <span className="text-xs text-emerald-200">
                                Prices for {summary.withPrice}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Price Range</p>
                          {summary.minPrice !== undefined ? (
                            <div className="space-y-1 text-sm text-gray-300">
                              <div>Min: {summary.minPrice.toFixed(2)}</div>
                              <div>Max: {summary.maxPrice?.toFixed(2)}</div>
                              {summary.avgPrice !== undefined && (
                                <div>Avg: {summary.avgPrice.toFixed(2)}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">No prices detected</span>
                          )}
                        </div>
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Domains</p>
                          <div className="flex flex-wrap gap-2">
                            {summary.domains.length ? (
                              summary.domains.map((d) => (
                                <span key={d} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">
                                  {d}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No domains captured</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Sample Products</p>
                        {summary.samples.length === 0 ? (
                          <p className="text-sm text-gray-400">No product items returned. Try refresh or confirm the site is ecommerce.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.samples.map((item, idx) => (
                              <div key={`${item.title}-${idx}`} className="flex gap-3 bg-gray-800/60 rounded-lg p-3">
                                <div className="w-16 h-16 rounded bg-gray-700/60 flex items-center justify-center overflow-hidden">
                                  {item.image ? (
                                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-xs text-gray-400">No image</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm text-white font-medium line-clamp-2">{item.title}</p>
                                  <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-2">
                                    {item.price && <span>${item.price}</span>}
                                    {item.rating && <span>Rating {item.rating}</span>}
                                    {item.reviews && <span>{item.reviews} reviews</span>}
                                    {item.availability && <span>{item.availability}</span>}
                                  </div>
                                  {item.url && (
                                    <a
                                      className="text-xs text-emerald-300 hover:text-emerald-200 mt-1 inline-block"
                                      href={item.url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      View product
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                return <div className="text-sm text-gray-400">No data available.</div>;
              })()}
            </div>
          </div>
        </>
      )}

      <AdDataDrawer
        isOpen={adDrawer.open}
        onClose={() => setAdDrawer((prev) => ({ ...prev, open: false }))}
        companyName={adDrawer.companyName}
        source={adDrawer.source}
        data={adDrawer.data}
      />
    </div>
  );
}
